import { BaseLazyWidgetsComponent } from 'lazy-widgets-wle';
import { Object3D } from '@wonderlandengine/api';
import { property } from '@wonderlandengine/api/decorators.js';
import {
    Alignment,
    AxisCoupling,
    Background,
    Checkbox,
    Column,
    FlexAlignment,
    Label,
    Row,
    RoundedCorners,
    ScrollableViewportWidget,
    ScrollbarMode,
    Slider,
    TextArea,
    TextAlignMode,
    TextButton,
    TextInput,
    ValidatedVariable,
    Variable,
    safeRoundRect,
    type Widget,
} from 'lazy-widgets';
import { EventsClient } from '../../events-client.js';
import Logger from '../../utils/logger.js';
import { MenuController } from '../menu-controller.js';
import {
    findMenuNodeById,
    isNodeVisibleOnPlatform,
    isSchemaVisibleOnPlatform,
    type MenuSchema,
} from '../MenuSchema.js';
import {
    ACCOUNT_MENU_ACTIONS,
    ACCOUNT_MENU_LABELS,
    createAccountMenuSchema,
    readAccountSchemaButtonLabel,
    readAccountSchemaInputPlaceholder,
} from '../schemas/account-menu-schema.js';
import { createEToyMenuSchema, ETOY_MENU_ACTIONS } from '../schemas/etoy-menu-schema.js';
import {
    createRoomsMenuSchema,
    readRoomsSchemaButtonLabel,
    readRoomsSchemaInputPlaceholder,
    readRoomsSchemaTextAt,
    ROOMS_MENU_ACTIONS,
    ROOMS_MENU_LABELS,
} from '../schemas/rooms-menu-schema.js';
import { createRemoteDeviceConnMenuSchema, REMOTE_DEVICE_CONN_MENU_ACTIONS } from '../schemas/remote-device-conn-menu-schema.js';
import { createSceneMenuSchema } from '../schemas/scene-menu-schema.js';
import { AVATAR_MENU_LABELS, createAvatarMenuSchema } from '../schemas/avatar-menu-schema.js';
import { getAvatarCatalog } from '../../avatar/avatar-catalog.js';
import {
    createUserAdminMenuSchema,
    readUserAdminSchemaButtonLabel,
    readUserAdminSchemaInputPlaceholder,
    readUserAdminSchemaTextAt,
    USER_ADMIN_MENU_ACTIONS,
    USER_ADMIN_MENU_LABELS,
} from '../schemas/user-admin-menu-schema.js';
import {
    createEntertainersMenuSchema,
    ENTERTAINERS_MENU_ACTIONS,
    ENTERTAINERS_MENU_LABELS,
    readEntertainersSchemaButtonLabel,
    readEntertainersSchemaInputPlaceholder,
} from '../schemas/entertainers-menu-schema.js';
import {
    createDeviceMenuSchema,
    DEVICE_MENU_ACTIONS,
    DEVICE_MENU_LABELS,
    readDeviceSchemaButtonLabel,
    readDeviceSchemaInputPlaceholder,
    readDeviceSchemaTextAt,
} from '../schemas/device-menu-schema.js';
import {
    createSettingsMenuSchema,
    formatRotationModeLabel,
    formatRotationSpeedLabel,
    readSettingsSchemaButtonLabel,
    readSettingsSchemaTextAt,
    SETTINGS_MENU_ACTIONS,
    SETTINGS_MENU_LABELS,
} from '../schemas/settings-menu-schema.js';
import {
    readSchemaButtonLabel,
    readSchemaInputPlaceholder,
    readSchemaTextAt,
    VR_MAIN_MENU_ACTIONS,
    createVrMainMenuSchema,
} from '../schemas/vr-main-menu-schema.js';
import { Submenu } from '../menu-controller.js';
import { EToyType } from '../../etoy/etoy-types.js';
import { SceneController } from '../../scene-controller.js';
import { DeviceControlMode, IntifaceController, SupportedAction } from '../../IntifaceController.js';
import { SmoothRotate } from '../../smooth-rotate.js';
import { SnapRotate } from '../../snap-rotation.js';
import { UserSettingsStore } from '../../utils/user-settings-store.js';

const MODULE = 'VrMenuRenderer';

export interface VrPreAuthMenuState {
    stage: 'age' | 'login';
    username: string;
    roomName: string;
    roomPassword: string;
    sharemic: boolean;
    headlessmode: boolean;
    statusMessage: string;
}

type AudioHintMode = 'auto' | 'speech' | 'music' | 'none';
type NoiseSuppressionMode = 'default' | 'mild' | 'aggressive';
type AudioRoutingMode = 'premix' | 'split';

interface RuntimeAudioTuningSettings {
    audioRoutingMode: AudioRoutingMode;
    echoCancellation: boolean;
    noiseSuppression: boolean;
    noiseSuppressionMode: NoiseSuppressionMode;
    broadcastDisableProcessing: boolean;
    autoGainControl: boolean;
    enableSenderBitrateCap: boolean;
    speechMaxBitrate: number;
    musicMaxBitrate: number;
    contentHintMode: AudioHintMode;
}

interface AudioTuningApi {
    get(): RuntimeAudioTuningSettings;
    set(partial: Partial<RuntimeAudioTuningSettings>): RuntimeAudioTuningSettings;
}

type ThemeColor = [number, number, number, number];

// ── Layout helpers ──────────────────────────────────────────────────────────
const centeredRow = (children: Widget[]): Row =>
    new Row(children, {
        multiContainerAlignment: { main: FlexAlignment.Center, cross: Alignment.Stretch },
    });

const actionRow = (children: Widget[]): Row =>
    new Row(children, {
        multiContainerAlignment: { main: FlexAlignment.Start, cross: Alignment.Center },
        multiContainerSpacing: 6,
    });

class RoundedTextButton extends TextButton {
    override handlePainting(dirtyRects: Array<[number, number, number, number]>): void {
        const ctx = (this as any).viewport?.context;
        if (ctx) {
            const anyThis = this as any;
            ctx.save();
            ctx.beginPath();
            safeRoundRect(ctx, anyThis.x, anyThis.y, anyThis.width, anyThis.height, anyThis.roundedCornersRadii ?? 12);
            ctx.clip();

            // Paint the button background and child while clipped so corners
            // remain rounded in all interaction states (hover/active/forced).
            const fill = typeof anyThis.getBackgroundFill === 'function'
                ? anyThis.getBackgroundFill()
                : anyThis.backgroundFill;
            ctx.fillStyle = fill;
            ctx.fillRect(anyThis.x, anyThis.y, anyThis.width, anyThis.height);
            anyThis.child?.paint?.(dirtyRects);

            ctx.restore();
            return;
        }

        super.handlePainting(dirtyRects);
    }
}

/**
 * VrMenuRenderer  (Phase 0 skeleton)
 *
 * A Wonderland Engine component that will render the in-world VR menu panel
 * using the Lazy Widgets library.  Place this component on the "MenuMesh"
 * object (child of ScreenHolder > MenuScreen).  The parent objects in that
 * hierarchy already handle world-space positioning.
 *
 * Dependency note
 * ───────────────
 * lazy-widgets-wle 0.10.3 declares a peer-dep of @wonderlandengine/api
 * >=1.4.0 <1.5.0, but this project uses 1.5.x.  Installed with
 * --legacy-peer-deps because the WLE API surface actually used by the library
 * (Component, property, Material, Object3D) is stable across that range.
 * Re-evaluate when lazy-widgets-wle ships a 1.5-compatible release.
 *
 * Required editor setup (Phase 1)
 * ────────────────────────────────
 * 1. Assign this component to the "MenuMesh" object in the WLE editor.
 * 2. Set the "material" property to a Flat/Phong Transparent Textured
 *    material (the library needs a known pipeline name to resolve the
 *    texture uniform, or set "textureUniformName" manually).
 * 3. Set "collisionGroupsMask" to the appropriate cursor interaction group.
 * 4. In MenuController, set the "vrMenuObject" property to "MenuMesh".
 *
 * Phase 0 behaviour
 * ─────────────────
 * The component mounts cleanly but renders a placeholder label.
 * No schema is consumed yet; show/hide stubs are wired up.
 * Phase 1 will implement full schema-driven widget tree construction.
 */
export class VrMenuRenderer extends BaseLazyWidgetsComponent {
    static TypeName = 'vr-menu-renderer';

    @property.float(0)
    menuSlideDefaultX = 0;

    @property.float(-1.2)
    menuSlideKeyboardVisibleX = -1.2;

    @property.float(1.2)
    menuSlideAvatarPreviewVisibleX = 1.2;

    @property.float(0.25)
    menuSlideDurationSeconds = 0.25;

    @property.string('virtual-keyboard-ui-root')
    keyboardRootComponentName = 'virtual-keyboard-ui-root';

    // Explicit slider height override for VR submenus. 16px is 60% taller than lazy-widgets default 10px.
    @property.float(16)
    vrSliderHeight = 16;

    // Visual tuning controls (editor-exposed) for the VR menu skin.
    //
    // Practical sizing guidance for headset readability:
    // - vrMenuButtonFontSize: 14-22 (18-20 is a good default)
    // - vrMenuButtonMinHeight: 24-46 (smaller values increase density quickly)
    // - vrMenuButtonInnerPadding: 2-12 (4-8 usually keeps labels readable)
    // - vrMenuButtonInnerPaddingVertical: 0-8 (-1 = auto from horizontal padding)
    // - vrMenuButtonCornerRadius: 6-16 (smaller = more angular look)
    // - vrMenuButtonMinWidth: 16-120 (set lower only if you want short labels to stay compact)
    // - vrMenuTableRowHeight: 24-44 (raise this first if list text clips)
    //
    // Notes:
    // - These properties are clamped in createButton/createTableCellButton to prevent
    //   unreadable text or invalid widget sizes.
    // - If buttons still feel too large, reduce min width plus inner padding together.
    @property.float(0.86)
    vrMenuPanelOpacity = 0.86;

    @property.float(0.34)
    vrMenuBorderOpacity = 0.34;

    // Theme colors (editor color pickers)
    @property.color(0.094, 0.173, 0.278, 1)
    vrMenuBackgroundColor: ThemeColor = [0.094, 0.173, 0.278, 1];

    @property.color(0.133, 0.486, 0.824, 1)
    vrMenuButtonColor: ThemeColor = [0.133, 0.486, 0.824, 1];

    @property.color(0.941, 0.973, 1.0, 1)
    vrMenuFontColor: ThemeColor = [0.941, 0.973, 1.0, 1];

    @property.color(0.424, 0.824, 1.0, 1)
    vrMenuButtonHighlightColor: ThemeColor = [0.424, 0.824, 1.0, 1];

    @property.color(0.384, 0.69, 0.91, 1)
    vrMenuBorderColor: ThemeColor = [0.384, 0.69, 0.91, 1];

    @property.color(0.659, 0.875, 1.0, 1)
    vrMenuBorderGlowColor: ThemeColor = [0.659, 0.875, 1.0, 1];

    @property.float(14)
    vrMenuButtonCornerRadius = 14;

    @property.float(14)
    vrMenuButtonInnerPadding = 14;

    @property.float(-1)
    vrMenuButtonInnerPaddingVertical = -1;

    @property.float(20)
    vrMenuButtonFontSize = 20;

    @property.float(42)
    vrMenuButtonMinHeight = 42;

    @property.float(24)
    vrMenuButtonMinWidth = 24;

    @property.float(24)
    vrMenuCheckboxLength = 24;

    @property.float(4)
    vrMenuCheckboxInnerPadding = 4;

    // Fixed reserved vertical space (lazy-widgets px) in Main submenu,
    // placed below Screen/Camera source buttons and above broadcast controls.
    // Increase this to flow UI around the in-world situational preview mesh.
    @property.float(140)
    vrMainPreviewSpacerHeight = 140;

    @property.float(34)
    vrMenuTableRowHeight = 34;

    @property.float(10)
    vrMenuTableScrollbarGap = 10;

    @property.float(1.0)
    vrMenuAccentStrength = 1.0;

    // Settings layout density tuning for headset readability.
    // true = compact (more controls visible), false = roomy (more breathing room).
    @property.bool(true)
    settingsCompactLayout = true;

    // Mesh object for shared media previewing (in-VR menu)
    // This mesh should be deactivated by default, and will be
    // activated when-and-only-when the in-VR menu's main (video sharing) 
    // submenu is activated.
    @property.object()
    sharedMediaPreviewMeshObject!: Object3D;

    private _schema: MenuSchema = createVrMainMenuSchema();
    private _menuVisible = false;
    private activeMenuMode: 'submenu' | 'preauth' = 'submenu';
    private footerMessageText = '';
    private currentVrSubmenu: Submenu = Submenu.Main;
    private preAuthMenuState: VrPreAuthMenuState | null = null;
    private navButtons: Map<Submenu, TextButton> = new Map();
    private dynamicRemoteNavButtons: Map<number, TextButton> = new Map();
    private currentRemotePeerId: number | null = null;
    private lastRemoteNavSignature = '';
    private lastRemoteCloneControlsSignature = '';

    // Main (video sharing) widgets
    private titleLabel!: Label;
    private footerLabel!: Label;
    private descriptionLabel!: Label;
    private screenshareButton!: TextButton;
    private camerashareButton!: TextButton;
    private broadcastButton!: TextButton;
    private urlInput!: TextInput;
    private playUrlButton!: TextButton;
    private serverBroadcastVariable!: Variable<boolean>;
    private serverBroadcastCheckbox!: Checkbox;
    private resyncPingsVariable!: Variable<boolean>;
    private resyncPingsCheckbox!: Checkbox;
    private audioMixLabel!: Label;
    private audioMixSlider!: Slider;
    private audioMixVariable!: Variable<number>;
    private urlValue: any = null;
    private resyncPingsEnabled = true;

    // Pre-auth widgets
    private preAuthUsernameValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private preAuthPasswordValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private preAuthRoomValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private preAuthRoomPasswordValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private preAuthShareMicVariable!: Variable<boolean>;
    private preAuthShareMicCheckbox!: Checkbox;
    private preAuthHeadlessVariable!: Variable<boolean>;
    private preAuthHeadlessCheckbox!: Checkbox;

    // Device (haptics) widgets
    private deviceStatusLabel!: Label;
    private deviceConnectButton!: TextButton;
    private deviceUrlValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private deviceUrlInput!: TextInput;
    private devicePermitButton!: TextButton;
    private deviceRevokeButton!: TextButton;
    private deviceEnvironmentButton!: TextButton;
    private deviceLeaveEnvironmentButton!: TextButton;
    private deviceHapticsHudButton!: TextButton;
    private deviceProstheticNoneButton!: TextButton;
    private deviceProstheticInnyButton!: TextButton;
    private deviceProstheticOutyButton!: TextButton;
    private deviceModeLabel!: Label;
    private deviceListLabel!: Label;
    private deviceVibVariable!: Variable<number>;
    private deviceVibSlider!: Slider;
    private deviceVibLabel!: Label;
    private deviceDurationValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private deviceDurationInput!: TextInput;
    private deviceNeedsRebuild = false;

    // Scene widgets
    private sceneStatusLabel!: Label;

    private avatarPreviewLabel!: Label;
    private avatarActiveLabel!: Label;
    private avatarPreviewIndex = 0;
    private sceneButtonRefs: Array<{ sceneRef: string; button: TextButton }> = [];

    // Remote device connection widgets
    private remoteConnKeyValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private remoteConnKeyInput!: TextInput;
    private remoteConnStatusLabel!: Label;
    private remoteCloneStatusLabel!: Label;
    private remoteCloneListLabel!: Label;
    private remoteCloneVibVariable!: Variable<number>;
    private remoteCloneVibSlider!: Slider;
    private remoteCloneVibLabel!: Label;
    private remoteCloneDurationValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private remoteCloneDurationInput!: TextInput;
    private remoteCloneEnvironmentButton!: TextButton;
    private remoteCloneLeaveEnvironmentButton!: TextButton;

    // Settings widgets
    private settingsRotationButton!: TextButton;
    private settingsSpeedLabel!: Label;
    private settingsSpeedVariable!: Variable<number>;
    private settingsSpeedSlider!: Slider;
    private settingsAudioRoutingButton!: TextButton;
    private settingsContentHintButton!: TextButton;
    private settingsNoiseModeButton!: TextButton;
    private settingsSpeechBitrateValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private settingsSpeechBitrateInput!: TextInput;
    private settingsMusicBitrateValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private settingsMusicBitrateInput!: TextInput;
    private settingsEchoVariable!: Variable<boolean>;
    private settingsEchoCheckbox!: Checkbox;
    private settingsNoiseVariable!: Variable<boolean>;
    private settingsNoiseCheckbox!: Checkbox;
    private settingsBroadcastDisableVariable!: Variable<boolean>;
    private settingsBroadcastDisableCheckbox!: Checkbox;
    private settingsAutoGainVariable!: Variable<boolean>;
    private settingsAutoGainCheckbox!: Checkbox;
    private settingsBitrateCapVariable!: Variable<boolean>;
    private settingsBitrateCapCheckbox!: Checkbox;
    private settingsDebugTextValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private settingsDebugTextArea!: TextArea;
    private settingsDebugStatusLabel!: Label;

    // Account widgets
    private accountUsernameLabel!: Label;
    private accountDisplayNameValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private accountDisplayNameInput!: TextInput;
    private accountEmailValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private accountEmailInput!: TextInput;
    private accountTokenBalanceLabel!: Label;
    private accountStatusLabel!: Label;
    private accountCurrentPasswordValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private accountCurrentPasswordInput!: TextInput;
    private accountNewPasswordValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private accountNewPasswordInput!: TextInput;
    private accountConfirmPasswordValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private accountConfirmPasswordInput!: TextInput;
    private accountPasswordStatusLabel!: Label;
    private accountLoadState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
    private accountData: { username: string; display_name: string | null; email: string | null; token_balance: number } | null = null;

    // Rooms widgets
    private roomsStatusLabel!: Label;
    private roomsCurrentLabel!: Label;
    private roomsJoinPasswordValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private roomsJoinPasswordInput!: TextInput;
    private roomsCreateNameValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private roomsCreateNameInput!: TextInput;
    private roomsCreatePasswordValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private roomsCreatePasswordInput!: TextInput;
    private roomsJoinButton!: TextButton;
    private roomsCreateButton!: TextButton;
    private roomsRefreshButton!: TextButton;
    private roomsLoadState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
    private roomsData: Array<{ name: string; isPrivate: boolean; userCount: number }> = [];
    private roomsSelectedRoomName: string | null = null;
    private roomsJoinPasswordDraft = '';
    private roomsCreateNameDraft = '';
    private roomsCreatePasswordDraft = '';

    // UserAdmin widgets
    private userAdminStatusLabel!: Label;
    private userAdminUsernameValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private userAdminUsernameInput!: TextInput;
    private userAdminDisplayNameValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private userAdminDisplayNameInput!: TextInput;
    private userAdminPasswordValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private userAdminPasswordInput!: TextInput;
    private userAdminNewPasswordValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private userAdminNewPasswordInput!: TextInput;
    private userAdminIsAdminVariable!: Variable<boolean>;
    private userAdminIsAdminCheckbox!: Checkbox;
    private userAdminIsEntertainerVariable!: Variable<boolean>;
    private userAdminIsEntertainerCheckbox!: Checkbox;
    private userAdminUpsertButton!: TextButton;
    private userAdminRefreshButton!: TextButton;
    private userAdminSetPasswordButton!: TextButton;
    private userAdminDepositButton!: TextButton;
    private userAdminPasswordStatusLabel!: Label;
    private userAdminTokenBalanceLabel!: Label;
    private userAdminDepositAmountValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private userAdminDepositAmountInput!: TextInput;
    private userAdminLoadState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
    private userAdminData: Array<{ id: string; username: string; display_name: string | null; is_user_admin: boolean; is_entertainer: boolean; token_balance: number }> = [];
    private userAdminSelectedUserId: string | null = null;
    private userAdminUsernameDraft = '';
    private userAdminDisplayNameDraft = '';
    private userAdminPasswordDraft = '';
    private userAdminNewPasswordDraft = '';
    private userAdminIsAdminDraft = false;
    private userAdminIsEntertainerDraft = false;
    private userAdminSelectedTokenBalance = 0;

    // Entertainers widgets
    private entertainersStatusLabel!: Label;
    private entertainersMyBalanceLabel!: Label;
    private entertainersSelectedLabel!: Label;
    private entertainersTipAmountValue: ValidatedVariable<string> = new ValidatedVariable('', null);
    private entertainersTipAmountInput!: TextInput;
    private entertainersTipButton!: TextButton;
    private entertainersRefreshButton!: TextButton;
    private entertainersLoadState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
    private entertainersData: Array<{ id: string; username: string; display_name: string | null }> = [];
    private entertainersSelectedUserId: string | null = null;
    private entertainersMyBalance = 0;
    private userAdminNavVisible = false;
    private userAdminNavResolved = false;
    private materialBindingLogged = false;
    private offscreenCanvasBackup: unknown = undefined;
    private offscreenCanvasPatched = false;
    private keyboardRootComponent: { root?: { enabled?: boolean } | null; active?: boolean } | null = null;
    private keyboardVisibleLast = false;
    private menuSlideStateSignatureLast = '';
    private menuSlideBaseY = 0;
    private menuSlideBaseZ = 0;
    private menuSlideCurrentX = 0;
    private menuSlideTargetX = 0;
    private menuSlideFromX = 0;
    private menuSlideStartMs = 0;
    private menuSlideAnimating = false;
    private sharedMediaPreviewMeshWarned = false;

    private syncSharedMediaPreviewMeshVisibility(): void {
        const previewMeshObject = this.sharedMediaPreviewMeshObject;
        if (!previewMeshObject) {
            return;
        }

        const meshComponent = previewMeshObject.getComponent('mesh' as never) as unknown | null;
        if (!meshComponent) {
            if (!this.sharedMediaPreviewMeshWarned) {
                Logger.warn(MODULE, 'sharedMediaPreviewMeshObject has no mesh component; shared media preview activation is disabled.');
                this.sharedMediaPreviewMeshWarned = true;
            }
            return;
        }

        const shouldBeActive = this._menuVisible && this.activeMenuMode === 'submenu' && this.currentVrSubmenu === Submenu.Main;
        // Keep the object itself active so non-visual components on this object
        // (for example shared-media event handlers) continue running in web mode.
        if (!previewMeshObject.active) {
            previewMeshObject.active = true;
        }

        const meshVisibilityHost = meshComponent as { active?: boolean };
        if (typeof meshVisibilityHost.active === 'boolean') {
            if (meshVisibilityHost.active !== shouldBeActive) {
                meshVisibilityHost.active = shouldBeActive;
            }
            return;
        }

        if (previewMeshObject.active !== shouldBeActive) {
            previewMeshObject.active = shouldBeActive;
        }
    }

    private findRotationPair(): { smooth: SmoothRotate; snap: SnapRotate } | null {
        for (const smooth of SmoothRotate.instances) {
            for (const snap of SnapRotate.instances) {
                if (snap.object === smooth.object) {
                    return { smooth, snap };
                }
            }
        }

        const firstSmooth = Array.from(SmoothRotate.instances)[0];
        const firstSnap = Array.from(SnapRotate.instances)[0];
        if (firstSmooth && firstSnap) {
            return { smooth: firstSmooth, snap: firstSnap };
        }

        return null;
    }

    private applyLiveRotationMode(mode: 'smooth' | 'snap'): void {
        const pair = this.findRotationPair();
        if (!pair) {
            Logger.warn(MODULE, 'Could not find smooth/snap rotation components for VR settings update.');
            return;
        }

        if (mode === 'smooth') {
            pair.smooth.setControlActive(true);
            pair.snap.setControlActive(false);
        } else {
            pair.smooth.setControlActive(false);
            pair.snap.setControlActive(true);
        }
    }

    private applyLiveRotationValue(mode: 'smooth' | 'snap', value: number): void {
        const pair = this.findRotationPair();
        if (!pair) {
            return;
        }

        if (mode === 'smooth') {
            pair.smooth.setRotationSpeed(value);
        } else {
            pair.snap.setSnapAngle(value);
        }
    }

    private getSchemaForSubmenu(sub: Submenu): MenuSchema | null {
        switch (sub) {
            case Submenu.Main: return createVrMainMenuSchema();
            case Submenu.Device: return createDeviceMenuSchema();
            case Submenu.Avatar: return createAvatarMenuSchema();
            case Submenu.EToy: return createEToyMenuSchema();
            case Submenu.Scene: return createSceneMenuSchema();
            case Submenu.RemoteDeviceConn: return createRemoteDeviceConnMenuSchema();
            case Submenu.Settings: return createSettingsMenuSchema();
            case Submenu.Account: return createAccountMenuSchema();
            case Submenu.Entertainers: return createEntertainersMenuSchema();
            case Submenu.Rooms: return createRoomsMenuSchema();
            case Submenu.UserAdmin: return createUserAdminMenuSchema();
            default: return null;
        }
    }

    private isVrSubmenuVisible(sub: Submenu): boolean {
        const schema = this.getSchemaForSubmenu(sub);
        const schemaVisible = schema ? isSchemaVisibleOnPlatform(schema, 'vr') : true;
        if (!schemaVisible) {
            return false;
        }
        if (sub === Submenu.UserAdmin) {
            return this.userAdminNavVisible;
        }
        return true;
    }

    private isVrNodeVisible(schema: MenuSchema, id: string): boolean {
        return isNodeVisibleOnPlatform(findMenuNodeById(schema, id), 'vr');
    }

    private getRemoteNavSignature(): string {
        const peerIds = IntifaceController.instance?.getControllableUsers?.() ?? [];
        const eventsClient = EventsClient.instance;
        const parts = peerIds
            .slice()
            .sort((a, b) => a - b)
            .map((peerId) => `${peerId}:${eventsClient?.remoteUsernames?.get(peerId) ?? ''}`);
        return parts.join('|');
    }

    private getRemoteCloneControlsSignature(): string {
        const peerId = this.currentRemotePeerId;
        if (peerId === null) {
            return 'none';
        }

        const devices = Array.from(IntifaceController.instance.getDevices(peerId));
        const mode = IntifaceController.instance.deviceControlMode;
        const deviceShape = devices
            .map((d: any) => {
                const deviceId = Number(d?.index);
                const actuatorCount = IntifaceController.instance.getActuatorCount(d, SupportedAction.Vibrate);
                return `${Number.isFinite(deviceId) ? deviceId : 'x'}:${actuatorCount}`;
            })
            .sort()
            .join('|');

        return `${peerId}|${mode}|${deviceShape}`;
    }

    private getVisibleVrNavItems(): Array<{ label: string; sub: Submenu; peerId?: number }> {
        const navItems: Array<{ label: string; sub: Submenu; peerId?: number }> = [
            { label: 'Video', sub: Submenu.Main },
            { label: 'Device', sub: Submenu.Device },
            { label: 'Avatar', sub: Submenu.Avatar },
            { label: 'EToys', sub: Submenu.EToy },
            { label: 'Scene', sub: Submenu.Scene },
            { label: 'Remote', sub: Submenu.RemoteDeviceConn },
            { label: 'Settings', sub: Submenu.Settings },
            { label: 'Account', sub: Submenu.Account },
            { label: 'Entertainers', sub: Submenu.Entertainers },
            { label: 'User Admin', sub: Submenu.UserAdmin },
            { label: 'Rooms', sub: Submenu.Rooms },
        ];

        const visibleItems = navItems.filter((item) => this.isVrSubmenuVisible(item.sub));

        const remotePeerIds = IntifaceController.instance?.getControllableUsers?.() ?? [];
        const eventsClient = EventsClient.instance;
        for (const peerId of remotePeerIds) {
            const username = eventsClient?.remoteUsernames?.get(peerId) || `Remote ${peerId}`;
            visibleItems.push({ label: username, sub: Submenu.RemoteDeviceClone, peerId });
        }

        return visibleItems;
    }

    private ensureCurrentVrSubmenuVisible(): void {
        if (this.currentVrSubmenu === Submenu.RemoteDeviceClone) {
            const remotePeerIds = IntifaceController.instance?.getControllableUsers?.() ?? [];
            if (this.currentRemotePeerId !== null && remotePeerIds.includes(this.currentRemotePeerId)) {
                return;
            }
        } else if (this.isVrSubmenuVisible(this.currentVrSubmenu)) {
            return;
        }

        const fallback = this.getVisibleVrNavItems()[0];
        if (fallback) {
            this.currentVrSubmenu = fallback.sub;
            this.currentRemotePeerId = fallback.sub === Submenu.RemoteDeviceClone
                ? (fallback as { peerId?: number }).peerId ?? null
                : null;
            MenuController.instance.currentSubmenu = fallback.sub;
            this.syncSharedMediaPreviewMeshVisibility();
        }
    }

    private createVisibleNavRows(): Row[] {
        const visibleItems = this.getVisibleVrNavItems();
        const rows: Row[] = [];

        for (let index = 0; index < visibleItems.length; index += 5) {
            const slice = visibleItems.slice(index, index + 5);
            rows.push(new Row(
                slice.map((item) => this.createNavButton(item.label, item.sub, item.peerId)),
                { multiContainerAlignment: { main: FlexAlignment.Center, cross: Alignment.Center } },
            ));
        }

        return rows;
    }

    override init(): void {
        this.initMenuSlideState();
        this.ensureTextureUniformName();
        this.normalizeMaterialTint();
        this.syncSharedMediaPreviewMeshVisibility();
        this.forceHtmlCanvasForLazyWidgets();
        super.init();
    }

    // ── Widget tree ──────────────────────────────────────────────────────────

    override async createWidget(): Promise<Widget> {
        this.navButtons.clear();
        this.dynamicRemoteNavButtons.clear();
        this.sceneButtonRefs = [];
        this.deviceNeedsRebuild = false;
        this.lastRemoteNavSignature = this.getRemoteNavSignature();
        this.lastRemoteCloneControlsSignature = this.getRemoteCloneControlsSignature();

        this.ensureCurrentVrSubmenuVisible();

        this.titleLabel = new Label(this.getActivePanelTitle(), {
            bodyTextAlign: TextAlignMode.Center,
            bodyTextFont: '700 42px "Orbitron", "Rajdhani", sans-serif',
        });
        this.footerLabel = new Label(this.footerMessageText, {
            bodyTextAlign: TextAlignMode.Center,
            bodyTextFont: '400 18px "Rajdhani", sans-serif',
            bodyTextFill: this.getMenuThemeColors().textFill,
            minHeight: 24,
        });

        const navRows = this.activeMenuMode === 'submenu' ? this.createVisibleNavRows() : [];

        const content = this.activeMenuMode === 'preauth' && this.preAuthMenuState
            ? this.buildPreAuthContent(this.preAuthMenuState)
            : this.buildSubmenuContent(this.currentVrSubmenu);
        if (this.activeMenuMode === 'submenu') {
            this.syncDynamicState();
        }

        const theme = this.getMenuThemeColors();

        const menuBodyProps: Record<string, unknown> = {
            multiContainerAlignment: { main: FlexAlignment.Start, cross: Alignment.Stretch },
            multiContainerSpacing: 8,
            containerPadding: { left: 14, right: 14, top: 12, bottom: 12 },
        };
        const menuBody = new Column([
            ...navRows,
            centeredRow([this.titleLabel]),
            content,
            this.footerLabel,
        ], menuBodyProps);

        const panelInnerProps: Record<string, unknown> = {
            widthCoupling: AxisCoupling.Bi,
            minWidth: 340,
            maxWidth: 920,
            minHeight: 210,
            maxHeight: 940,
            multiContainerSpacing: 10,
            roundedCornersRadii: 18,
            bodyTextFill: theme.textFill,
            bodyTextFont: '500 20px "Rajdhani", sans-serif',
            primaryFill: theme.buttonFill,
            accentFill: theme.buttonHighlightFill,
            canvasFill: theme.panelFill,
            inputBackgroundFill: theme.inputFill,
            inputTextFill: theme.textFill,
            inputTextInnerPadding: { left: 12, right: 12, top: 8, bottom: 8 },
            checkboxLength: 24,
            checkboxInnerPadding: 4,
        };
        const panelInner = new Background(menuBody, panelInnerProps);
        const insetBackgroundProps: Record<string, unknown> = {
            containerPadding: { left: 10, right: 10, top: 10, bottom: 10 },
            roundedCornersRadii: 22,
            canvasFill: theme.borderFill,
            backgroundGlowFill: theme.borderGlowFill,
        };

        return new RoundedCorners(
            new Background(panelInner, insetBackgroundProps),
            {
                canvasFill: 'rgba(0, 0, 0, 0)',
                roundedCornersRadii: 22,
                backgroundFill: theme.outerPanelFill,
                bodyTextFill: theme.textFill,
                primaryFill: theme.buttonFill,
                accentFill: theme.buttonHighlightFill,
                inputBackgroundFill: theme.inputFill,
                inputTextFill: theme.textFill,
                containerPadding: { left: 8, right: 8, top: 8, bottom: 8 },
                checkboxLength: 24,
                checkboxInnerPadding: 4,
                scrollBarThickness: 12,
                scrollbarCornersRadii: 10,
            },
        );
    }

    // ── Schema interface ─────────────────────────────────────────────────────
    setSchema(schema: MenuSchema): void {
        const wasPreAuth = this.activeMenuMode !== 'submenu' || this.preAuthMenuState !== null;
        this.activeMenuMode = 'submenu';
        this.preAuthMenuState = null;
        const matchingSubmenu = Object.values(Submenu).find((value) => value === schema.id) as Submenu | undefined;
        if (matchingSubmenu) {
            this.currentVrSubmenu = matchingSubmenu;
        }
        this._schema = schema;
        this.syncSchema();
        if (wasPreAuth && this.root) {
            void this.rebuildRoot();
            return;
        }
        this.root?.update();
    }

    showPreAuthMenu(state: VrPreAuthMenuState): void {
        const nextSignature = JSON.stringify(state);
        const currentSignature = JSON.stringify(this.preAuthMenuState);
        const needsRebuild = this.activeMenuMode !== 'preauth' || currentSignature !== nextSignature;

        this.activeMenuMode = 'preauth';
        this.preAuthMenuState = { ...state };
        this.footerMessageText = '';

        if (!this._menuVisible) {
            this.showMenu();
        }

        if (needsRebuild) {
            void this.rebuildRoot();
            return;
        }

        this.root?.update();
    }

    hidePreAuthMenu(): void {
        if (this.activeMenuMode !== 'preauth') {
            return;
        }

        this.activeMenuMode = 'submenu';
        this.preAuthMenuState = null;
        this.hideMenu();
        if (this.root) {
            void this.rebuildRoot();
        }
    }

    isPreAuthMenuActive(): boolean {
        return this._menuVisible && this.activeMenuMode === 'preauth';
    }

    setFooterMessage(message: string): void {
        this.footerMessageText = message ?? '';
        if (this.footerLabel) {
            this.footerLabel.text = this.footerMessageText;
        }
        this.root?.update();
    }

    // ── Submenu navigation ──────────────────────────────────────────────────

    /** Switch to a different submenu, rebuilding the widget tree. */
    async switchVrSubmenu(submenu: Submenu, peerId?: number): Promise<void> {
        if (this.currentVrSubmenu === submenu && (submenu !== Submenu.RemoteDeviceClone || this.currentRemotePeerId === (peerId ?? null))) {
            return;
        }
        if (!this.isVrSubmenuVisible(submenu)) {
            return;
        }
        this.activeMenuMode = 'submenu';
        this.preAuthMenuState = null;
        this.currentVrSubmenu = submenu;
        this.currentRemotePeerId = submenu === Submenu.RemoteDeviceClone ? (peerId ?? this.currentRemotePeerId) : null;
        MenuController.instance.currentSubmenu = submenu;
        this.syncSharedMediaPreviewMeshVisibility();
        this.resetAsyncLoadState(submenu);
        this.syncAvatarPreviewVisibility();
        await this.rebuildRoot();
    }

    private async rebuildRoot(): Promise<void> {
        const wasVisible = this._menuVisible;
        if (this.root) {
            (this.root as unknown as { destroy(): void }).destroy();
            (this as unknown as { root: unknown }).root = null;
        }
        this.forceHtmlCanvasForLazyWidgets();
        // rootDestroyed is only set by onDestroy(); it's still false here.
        const material = (this as unknown as { material: unknown }).material;
        if (!material) {
            this.restoreOffscreenCanvasPatch();
            return;
        }
        try {
            const props = this.getRootProperties();
            const newRoot = await this.createWLRoot(material as never, props as never);
            (this as unknown as { root: unknown }).root = newRoot;
            this.materialBindingLogged = false;
            newRoot.enabled = wasVisible;
            this.onRootReady(newRoot as never);
        } catch (error) {
            this.restoreOffscreenCanvasPatch();
            throw error;
        }
    }

    private getSubmenuTitle(sub: Submenu): string {
        if (sub === Submenu.RemoteDeviceClone) {
            const peerId = this.currentRemotePeerId;
            const username = peerId !== null ? (EventsClient.instance?.remoteUsernames?.get(peerId) ?? `Remote ${peerId}`) : 'Remote Device Control';
            return username;
        }

        const schema = this.getSchemaForSubmenu(sub);
        if (schema) {
            return schema.title;
        }

        const map: Record<string, string> = {
            [Submenu.Main]:              'Video Sharing',
            [Submenu.Device]:            'Haptic Devices',
            [Submenu.EToy]:              'EToys',
            [Submenu.Scene]:             'Scene',
            [Submenu.RemoteDeviceConn]:  'Remote Device Connection',
            [Submenu.Settings]:          'Settings',
            [Submenu.Account]:           'Account',
            [Submenu.Entertainers]:      'Entertainers',
            [Submenu.Rooms]:             'Rooms',
            [Submenu.UserAdmin]:         'User Admin',
            [Submenu.RemoteDeviceClone]: 'Remote Device Control',
        };
        return map[sub] ?? sub;
    }

    private getActivePanelTitle(): string {
        if (this.activeMenuMode === 'preauth') {
            return this.preAuthMenuState?.stage === 'age' ? 'Age Verification' : 'Connect to World';
        }

        return this.getSubmenuTitle(this.currentVrSubmenu);
    }

    private createNavButton(label: string, sub: Submenu, peerId?: number): TextButton {
        const btn = this.createButton(label, () => {
            void this.switchVrSubmenu(sub, peerId);
        });
        if (sub === Submenu.RemoteDeviceClone && peerId !== undefined) {
            this.dynamicRemoteNavButtons.set(peerId, btn);
        } else {
            this.navButtons.set(sub, btn);
        }
        // Mark as visually "active" by using the forced/accentFill state
        if (sub === this.currentVrSubmenu && (sub !== Submenu.RemoteDeviceClone || this.currentRemotePeerId === (peerId ?? null))) {
            (btn as unknown as { forced?: boolean }).forced = true;
        }
        return btn;
    }

    private resetAsyncLoadState(sub: Submenu): void {
        if (sub === Submenu.Account) {
            this.accountLoadState = 'idle';
        } else if (sub === Submenu.Entertainers) {
            this.entertainersLoadState = 'idle';
            this.entertainersData = [];
        } else if (sub === Submenu.Rooms) {
            this.roomsLoadState = 'idle';
            this.roomsData = [];
        } else if (sub === Submenu.UserAdmin) {
            this.userAdminLoadState = 'idle';
            this.userAdminData = [];
        } else if (sub === Submenu.Avatar) {
            const catalog = getAvatarCatalog();
            const activeId = EventsClient.instance?.getSelectedAvatarId?.() ?? '';
            const activeIndex = catalog.findIndex((entry) => entry.id === activeId);
            this.avatarPreviewIndex = activeIndex >= 0 ? activeIndex : 0;
        }
    }

    // ── Submenu content builders ────────────────────────────────────────────

    private buildSubmenuContent(sub: Submenu): Widget {
        switch (sub) {
            case Submenu.Main:              return this.buildMainContent();
            case Submenu.Device:            return this.buildDeviceContent();
            case Submenu.Avatar:            return this.buildAvatarContent();
            case Submenu.EToy:              return this.buildEToyContent();
            case Submenu.Scene:             return this.buildSceneContent();
            case Submenu.RemoteDeviceConn:  return this.buildRemoteConnContent();
            case Submenu.RemoteDeviceClone: return this.buildRemoteCloneContent();
            case Submenu.Settings:          return this.buildSettingsContent();
            case Submenu.Account:           return this.buildAccountContent();
            case Submenu.Entertainers:      return this.buildEntertainersContent();
            case Submenu.Rooms:             return this.buildRoomsContent();
            case Submenu.UserAdmin:         return this.buildUserAdminContent();
            default:                        return new Label(`${this.getSubmenuTitle(sub)} — not available in VR yet.`);
        }
    }

    private buildPreAuthContent(state: VrPreAuthMenuState): Widget {
        if (state.stage === 'age') {
            return this.createSpacedColumn([
                new Label('You must be 18 or older to access this application.', {
                    bodyTextAlign: TextAlignMode.Center,
                }),
                centeredRow([
                    this.createButton('I am 18 or older', () => {
                        EventsClient.instanceOrNull?.confirmPreAuthAgeVerification();
                    }),
                ]),
                centeredRow([
                    this.createButton('I am under 18', () => {
                        EventsClient.instanceOrNull?.denyPreAuthAgeVerification();
                    }),
                ]),
            ], 10);
        }

        this.preAuthUsernameValue = new ValidatedVariable(state.username ?? '', null);
        this.preAuthPasswordValue = new ValidatedVariable('', null);
        this.preAuthRoomValue = new ValidatedVariable(state.roomName ?? '', null);
        this.preAuthRoomPasswordValue = new ValidatedVariable(state.roomPassword ?? '', null);
        this.preAuthShareMicVariable = new Variable<boolean>(!!state.sharemic);
        this.preAuthHeadlessVariable = new Variable<boolean>(!!state.headlessmode);
        this.preAuthShareMicCheckbox = this.createCheckbox(this.preAuthShareMicVariable);
        this.preAuthHeadlessCheckbox = this.createCheckbox(this.preAuthHeadlessVariable);

        const usernameInput = new TextInput(this.preAuthUsernameValue);
        const passwordInput = new TextInput(this.preAuthPasswordValue, { hideText: true });
        const roomInput = new TextInput(this.preAuthRoomValue);
        const roomPasswordInput = new TextInput(this.preAuthRoomPasswordValue, { hideText: true });
        const statusLabel = new Label(state.statusMessage || '', {
            bodyTextFill: state.statusMessage ? 'rgba(255, 112, 112, 0.95)' : this.getMenuThemeColors().textFill,
            minHeight: 24,
        });

        return this.createSpacedColumn([
            this.createLabeledControlRow('Username', usernameInput),
            this.createLabeledControlRow('Password', passwordInput),
            this.createLabeledControlRow('Room', roomInput),
            this.createLabeledControlRow('Room Password', roomPasswordInput),
            this.createLabeledControlRow('Share Microphone', this.preAuthShareMicCheckbox),
            this.createLabeledControlRow('Headless Mode', this.preAuthHeadlessCheckbox),
            actionRow([
                this.createButton('Connect', () => {
                    void EventsClient.instanceOrNull?.submitPreAuthLogin({
                        username: String(this.preAuthUsernameValue.value ?? '').trim(),
                        password: String(this.preAuthPasswordValue.value ?? ''),
                        room: String(this.preAuthRoomValue.value ?? '').trim(),
                        roomPassword: String(this.preAuthRoomPasswordValue.value ?? ''),
                        sharemic: !!this.preAuthShareMicVariable.value,
                        headlessmode: !!this.preAuthHeadlessVariable.value,
                    });
                }),
            ]),
            statusLabel,
        ], 8);
    }

    // ── Main (Video Sharing) ────────────────────────────────────────────────

    private buildMainContent(): Widget {
        const mainSchema = this._schema ?? createVrMainMenuSchema();
        this.urlValue = new ValidatedVariable('', null);
        const initialGain = EventsClient.instance?.getBroadcastShareAudioMixGain() ?? 1;
        this.audioMixVariable = new Variable<number>(initialGain);
        this.audioMixVariable.watch(() => {
            const gain = Math.max(0, Math.min(2, this.audioMixVariable.value));
            EventsClient.instance?.setBroadcastShareAudioMixGain(gain);
            if (this.audioMixLabel) {
                this.audioMixLabel.text = `Shared Audio in Group Mix: ${gain.toFixed(2)}x`;
            }
        });
        this.descriptionLabel = new Label(
            readSchemaTextAt(mainSchema, 0, 'Choose Portion of Screen or Camera to Share'),
        );
        this.screenshareButton = this.createButton(
            readSchemaButtonLabel(mainSchema, VR_MAIN_MENU_ACTIONS.chooseScreenshare, 'Screen'),
            () => {
            MenuController.instance.onUserChoice.notify(VR_MAIN_MENU_ACTIONS.chooseScreenshare);
        });
        this.camerashareButton = this.createButton(
            readSchemaButtonLabel(mainSchema, VR_MAIN_MENU_ACTIONS.chooseCamerashare, 'Camera'),
            () => {
            MenuController.instance.onUserChoice.notify(VR_MAIN_MENU_ACTIONS.chooseCamerashare);
        });
        this.broadcastButton = this.createButton('Broadcast', () => {
            if (EventsClient.instance.sharingVideo) {
                MenuController.instance.onUserChoice.notify('stop-broadcasting-media');
            } else {
                MenuController.instance.onUserChoice.notify('broadcast-chosen-media');
            }
        });
        this.urlInput = new TextInput(this.urlValue);
        (this.urlInput as unknown as { placeholder?: string }).placeholder = readSchemaInputPlaceholder(
            mainSchema,
            'vr-broadcast-url-input',
            'https://example.com/video-stream.mp4',
        );
        this.playUrlButton = this.createButton(
            readSchemaButtonLabel(mainSchema, VR_MAIN_MENU_ACTIONS.broadcastUrl, 'Play URL For Everyone'),
            () => { this.submitUrlPlayback(); },
        );
        this.serverBroadcastVariable = new Variable<boolean>(!!EventsClient.instance?.serverBasedBroadcast);
        this.serverBroadcastCheckbox = this.createCheckbox(this.serverBroadcastVariable);
        this.serverBroadcastVariable.watch(() => {
            EventsClient.instance.serverBasedBroadcast = !!this.serverBroadcastVariable.value;
        });

        this.resyncPingsVariable = new Variable<boolean>(!!this.resyncPingsEnabled);
        this.resyncPingsCheckbox = this.createCheckbox(this.resyncPingsVariable);
        this.resyncPingsVariable.watch(() => {
            this.resyncPingsEnabled = !!this.resyncPingsVariable.value;
        });

        this.audioMixLabel = new Label(`Shared Audio in Group Mix: ${initialGain.toFixed(2)}x`);
        this.audioMixSlider = new Slider(this.audioMixVariable, 0, 2, {
            snapIncrement: 0.05,
            minHeight: this.vrSliderHeight,
            maxHeight: this.vrSliderHeight,
            sliderThickness: this.vrSliderHeight,
        });
        const children: Widget[] = [this.descriptionLabel];
        const sourceButtons: TextButton[] = [
            this.isVrNodeVisible(mainSchema, VR_MAIN_MENU_ACTIONS.chooseScreenshare) ? this.screenshareButton : null,
            this.isVrNodeVisible(mainSchema, VR_MAIN_MENU_ACTIONS.chooseCamerashare) ? this.camerashareButton : null,
        ].filter((widget): widget is TextButton => widget !== null);
        if (sourceButtons.length > 0) {
            children.push(new Row(sourceButtons, {
                multiContainerAlignment: { main: FlexAlignment.Center, cross: Alignment.Stretch },
            }));

            const previewSpacerHeight = Math.max(0, Math.min(640, this.vrMainPreviewSpacerHeight));
            if (previewSpacerHeight > 0) {
                children.push(this.createVerticalSpacer(previewSpacerHeight));
            }
        }
        if (this.isVrNodeVisible(mainSchema, VR_MAIN_MENU_ACTIONS.toggleBroadcast)) {
            children.push(this.createSubheadingLabel('Broadcast Video to Peers'));
            children.push(centeredRow([this.broadcastButton]));
        }
        if (this.isVrNodeVisible(mainSchema, 'vr-broadcast-url-input')) {
            children.push(new Row([new Label('Direct Stream URL (beta)'), this.urlInput], {
                multiContainerAlignment: { main: FlexAlignment.Center, cross: Alignment.Center },
            }));
        }
        if (this.isVrNodeVisible(mainSchema, VR_MAIN_MENU_ACTIONS.broadcastUrl)) {
            children.push(centeredRow([this.playUrlButton]));
        }
        if (this.isVrNodeVisible(mainSchema, VR_MAIN_MENU_ACTIONS.toggleResync)) {
            children.push(this.createLabeledControlRow(
                'Enable periodic re-sync pings',
                this.resyncPingsCheckbox,
                260,
            ));
        }
        if (this.isVrNodeVisible(mainSchema, VR_MAIN_MENU_ACTIONS.toggleServerBroadcast)) {
            children.push(this.createLabeledControlRow(
                'Use Server-Based Broadcast (mediasoup)',
                this.serverBroadcastCheckbox,
                260,
            ));
        }
        if (this.isVrNodeVisible(mainSchema, VR_MAIN_MENU_ACTIONS.audioMixDown) || this.isVrNodeVisible(mainSchema, VR_MAIN_MENU_ACTIONS.audioMixUp)) {
            children.push(this.audioMixLabel, this.audioMixSlider);
        }
        return this.createSpacedColumn(children, 8);
    }

    // ── EToys ───────────────────────────────────────────────────────────────

    private buildEToyContent(): Widget {
        const schema = createEToyMenuSchema();
        const children: Widget[] = [];
        if (this.isVrNodeVisible(schema, ETOY_MENU_ACTIONS.addBall)) {
            children.push(centeredRow([this.createButton('Add Ball', () => { EventsClient.instance.requestToyCreate(EToyType.BALL); })]));
        }
        if (this.isVrNodeVisible(schema, ETOY_MENU_ACTIONS.addProximityVibe)) {
            children.push(centeredRow([this.createButton('Add Proximity Vibrator', () => { EventsClient.instance.requestToyCreate(EToyType.PROXIMITY_VIBE); })]));
        }
        if (this.isVrNodeVisible(schema, ETOY_MENU_ACTIONS.addOscillatingRotator)) {
            children.push(centeredRow([this.createButton('Add Oscillating Rotator', () => { EventsClient.instance.requestToyCreate(EToyType.OSCILLATING_ROT); })]));
        }
        if (this.isVrNodeVisible(schema, ETOY_MENU_ACTIONS.clearAll)) {
            children.push(centeredRow([this.createButton('Clear All EToys', () => { EventsClient.instance.requestToyDeleteAll(); })]));
        }
        return this.createSpacedColumn(children, 8);
    }

    // ── Avatar ──────────────────────────────────────────────────────────────

    private buildAvatarContent(): Widget {
        const catalog = getAvatarCatalog();
        this.avatarPreviewLabel = new Label('');
        this.avatarActiveLabel = new Label('');

        if (catalog.length === 0) {
            return this.createSpacedColumn([new Label(AVATAR_MENU_LABELS.noAvatars)], 8);
        }

        const step = (delta: number) => {
            const entries = getAvatarCatalog();
            if (entries.length === 0) {
                return;
            }
            this.avatarPreviewIndex = (this.avatarPreviewIndex + delta + entries.length) % entries.length;
            this.syncAvatarPreviewVisibility();
            this.syncAvatarState();
        };

        const children: Widget[] = [
            this.createSubheadingLabel(AVATAR_MENU_LABELS.subtitle),
            this.avatarPreviewLabel,
            this.avatarActiveLabel,
            actionRow([
                this.createButton(AVATAR_MENU_LABELS.previous, () => step(-1)),
                this.createButton(AVATAR_MENU_LABELS.next, () => step(1)),
            ]),
            actionRow([
                this.createButton(AVATAR_MENU_LABELS.select, () => {
                    const entry = getAvatarCatalog()[this.avatarPreviewIndex];
                    if (!entry) {
                        return;
                    }
                    EventsClient.instance?.applyAvatarState?.({ avatarId: entry.id });
                    EventsClient.instance?.requestAvatarSelection(entry.id);
                    this.syncAvatarState();
                }),
            ]),
        ];

        this.syncAvatarState();
        return this.createSpacedColumn(children, 8);
    }

    private syncAvatarState(): void {
        const catalog = getAvatarCatalog();
        const previewEntry = catalog[this.avatarPreviewIndex];
        const activeId = EventsClient.instance?.getSelectedAvatarId?.() ?? '';
        const activeEntry = catalog.find((entry) => entry.id === activeId);

        if (this.avatarPreviewLabel) {
            this.avatarPreviewLabel.text = previewEntry
                ? `${AVATAR_MENU_LABELS.previewPrefix} ${previewEntry.label}`
                : AVATAR_MENU_LABELS.noAvatars;
        }
        if (this.avatarActiveLabel) {
            this.avatarActiveLabel.text = `${AVATAR_MENU_LABELS.activePrefix} ${activeEntry?.label ?? activeId}`;
        }
    }

    /** The in-world preview object is shared with the web menu, so only one owner may drive it. */
    private syncAvatarPreviewVisibility(): void {
        const menuController = MenuController.instance;
        if (!menuController) {
            return;
        }

        const shouldPreview = this._menuVisible
            && this.activeMenuMode === 'submenu'
            && this.currentVrSubmenu === Submenu.Avatar;

        if (!shouldPreview) {
            menuController.clearAvatarPreview();
            return;
        }

        const entry = getAvatarCatalog()[this.avatarPreviewIndex];
        if (entry) {
            menuController.showAvatarPreview(entry.id);
        }
    }

    // ── Scene ───────────────────────────────────────────────────────────────

    private buildSceneContent(): Widget {
        this.sceneStatusLabel = new Label('');
        this.sceneButtonRefs = [];
        const sc = SceneController.instance;
        const sceneRefs = sc?.sceneRefs ?? [];
        const currentRef = sc?.currentSceneRef ?? '';
        const sceneButtons: Widget[] = [];
        for (const ref of sceneRefs) {
            const btn = this.createButton(ref === currentRef ? `▶ ${ref}` : ref, () => {
                if (ref !== SceneController.instance?.currentSceneRef) {
                    EventsClient.instance.requestSceneSwitch(ref);
                    this.sceneStatusLabel.text = `Requested switch to: ${ref}`;
                }
            });
            this.sceneButtonRefs.push({ sceneRef: ref, button: btn });
            sceneButtons.push(centeredRow([btn]));
        }
        if (sceneButtons.length === 0) {
            sceneButtons.push(new Label('No scenes available.'));
        }
        return this.createSpacedColumn([
            this.createSubheadingLabel('Choose a scene to request for all users'),
            ...sceneButtons,
            this.sceneStatusLabel,
        ], 8);
    }

    // ── Haptic Devices ──────────────────────────────────────────────────────

    private buildDeviceContent(): Widget {
        const deviceSchema = createDeviceMenuSchema();
        const ic = IntifaceController.instance;
        const connected = ic?.connected ?? false;
        const connecting = ic?.connecting ?? false;
        const storedUrl = UserSettingsStore.getCookie(UserSettingsStore.Keys.IntifaceUrl)
            || readDeviceSchemaInputPlaceholder(deviceSchema, 'device-intiface-url-input', DEVICE_MENU_LABELS.intifaceUrlPlaceholder);

        this.deviceUrlValue = new ValidatedVariable(storedUrl, null);
        this.deviceUrlInput = new TextInput(this.deviceUrlValue);
        (this.deviceUrlInput as unknown as { placeholder?: string }).placeholder = readDeviceSchemaInputPlaceholder(
            deviceSchema,
            'device-intiface-url-input',
            DEVICE_MENU_LABELS.intifaceUrlPlaceholder,
        );
        this.deviceStatusLabel = new Label(connected ? 'Status: Connected' : connecting ? 'Status: Connecting…' : 'Status: Disconnected');
        this.deviceConnectButton = this.createButton(
            connected
                ? DEVICE_MENU_LABELS.disconnect
                : connecting
                    ? DEVICE_MENU_LABELS.connecting
                    : readDeviceSchemaButtonLabel(deviceSchema, DEVICE_MENU_ACTIONS.connectToIntiface, DEVICE_MENU_LABELS.connect),
            () => {
            const url = String(this.deviceUrlValue.value ?? '').trim();
            if (IntifaceController.instance.connected) {
                MenuController.instance.onUserChoice.notify(DEVICE_MENU_ACTIONS.disconnectFromIntiface, { url });
            } else if (!IntifaceController.instance.connecting) {
                UserSettingsStore.setCookie(UserSettingsStore.Keys.IntifaceUrl, url, 365);
                MenuController.instance.onUserChoice.notify(DEVICE_MENU_ACTIONS.connectToIntiface, { url });
            }
        });
        this.devicePermitButton = this.createButton(
            readDeviceSchemaButtonLabel(deviceSchema, 'device-permit-remote-control', DEVICE_MENU_LABELS.permitRemoteControl),
            () => {
            if (IntifaceController.instance.connected) {
                IntifaceController.instance.ShareDeviceControls();
            }
        });
        this.deviceRevokeButton = this.createButton(
            readDeviceSchemaButtonLabel(deviceSchema, 'device-revoke-remote-control', DEVICE_MENU_LABELS.revokeRemoteControl),
            () => {
            // Reset to Self mode (no dedicated Revoke API yet)
            (IntifaceController.instance as any)._deviceControlMode = DeviceControlMode.Self;
            IntifaceController.instance.onControlModeChanged.notify();
        });

        this.deviceEnvironmentButton = this.createButton(
            readDeviceSchemaButtonLabel(deviceSchema, DEVICE_MENU_ACTIONS.enterEnvironment, DEVICE_MENU_LABELS.environment),
            () => {
                if (IntifaceController.instance.connected) {
                    (IntifaceController.instance as any)._deviceControlMode = DeviceControlMode.Environment;
                    IntifaceController.instance.onControlModeChanged.notify();
                }
            },
        );
        this.deviceLeaveEnvironmentButton = this.createButton(
            readDeviceSchemaButtonLabel(deviceSchema, DEVICE_MENU_ACTIONS.leaveEnvironment, DEVICE_MENU_LABELS.leaveEnvironment),
            () => {
                if (IntifaceController.instance.connected) {
                    (IntifaceController.instance as any)._deviceControlMode = DeviceControlMode.Self;
                    IntifaceController.instance.onControlModeChanged.notify();
                }
            },
        );
        this.deviceHapticsHudButton = this.createButton(
            readDeviceSchemaButtonLabel(deviceSchema, DEVICE_MENU_ACTIONS.toggleHapticsHud, DEVICE_MENU_LABELS.showHapticsHud),
            () => {
                const enabled = EventsClient.instance?.isHapticDisplayEnabled?.() ?? true;
                EventsClient.instance?.setHapticDisplayEnabled?.(!enabled);
                this.setButtonText(
                    this.deviceHapticsHudButton,
                    !enabled ? DEVICE_MENU_LABELS.hideHapticsHud : DEVICE_MENU_LABELS.showHapticsHud,
                );
            },
        );
        this.deviceProstheticNoneButton = this.createButton(
            readDeviceSchemaButtonLabel(deviceSchema, DEVICE_MENU_ACTIONS.selectProstheticNone, DEVICE_MENU_LABELS.prostheticNone),
            () => { EventsClient.instance?.requestProstheticSelection?.('none'); },
        );
        this.deviceProstheticInnyButton = this.createButton(
            readDeviceSchemaButtonLabel(deviceSchema, DEVICE_MENU_ACTIONS.selectProstheticInny, DEVICE_MENU_LABELS.prostheticInny),
            () => { EventsClient.instance?.requestProstheticSelection?.('inny'); },
        );
        this.deviceProstheticOutyButton = this.createButton(
            readDeviceSchemaButtonLabel(deviceSchema, DEVICE_MENU_ACTIONS.selectProstheticOuty, DEVICE_MENU_LABELS.prostheticOuty),
            () => { EventsClient.instance?.requestProstheticSelection?.('outy'); },
        );
        this.deviceModeLabel = new Label(`Control mode: ${ic?.deviceControlMode ?? DeviceControlMode.Self}`);

        // Per-device vibration controls
        const devices = Array.from(ic?.getDevices() ?? []);
        const deviceRows: Widget[] = [];
        this.deviceVibVariable = new Variable<number>(0);
        this.deviceVibSlider = new Slider(this.deviceVibVariable, 0, 100, {
            snapIncrement: 1,
            minHeight: this.vrSliderHeight,
            maxHeight: this.vrSliderHeight,
            sliderThickness: this.vrSliderHeight,
        });
        this.deviceVibLabel = new Label('Vibration: 0%');
        this.deviceDurationValue = new ValidatedVariable('', null);
        this.deviceDurationInput = new TextInput(this.deviceDurationValue);
        (this.deviceDurationInput as unknown as { placeholder?: string }).placeholder = 'Duration sec (blank=persistent)';
        this.deviceVibVariable.watch(() => {
            const val = Math.round(this.deviceVibVariable.value);
            if (this.deviceVibLabel) {
                this.deviceVibLabel.text = `Vibration: ${val}%`;
            }
        });

        if (devices.length > 0) {
            const names = devices.map((d: any) => String(d.name ?? 'Unknown')).join(', ');
            this.deviceListLabel = new Label(`Devices: ${names}`);
            deviceRows.push(
                this.deviceListLabel,
                this.deviceVibLabel,
                this.deviceVibSlider,
                new Row([new Label('Duration:'), this.deviceDurationInput], {
                    multiContainerAlignment: { main: FlexAlignment.Center, cross: Alignment.Center },
                }),
                centeredRow([this.createButton(readDeviceSchemaButtonLabel(deviceSchema, DEVICE_MENU_ACTIONS.sendDeviceCommand, DEVICE_MENU_LABELS.sendVibration), () => {
                    const percent = Math.max(0, Math.min(100, Math.round(this.deviceVibVariable.value)));
                    const durationValue = String(this.deviceDurationValue.value ?? '').trim();
                    const duration = durationValue !== '' ? durationValue : undefined;
                    const deviceList = IntifaceController.instance.getDevices();
                    for (const d of deviceList) {
                        const actuatorCount = IntifaceController.instance.getActuatorCount(d as any, SupportedAction.Vibrate);
                        const actuateVals: string[] = [];
                        for (let idx = 0; idx < Math.max(1, actuatorCount); idx++) {
                            const steps = IntifaceController.instance.getVibeSteps(d as any, idx) || 1;
                            const scaled = Math.round((percent / 100) * steps);
                            actuateVals.push(String(scaled));
                        }
                        MenuController.instance.onUserChoice.notify(DEVICE_MENU_ACTIONS.sendDeviceCommand, {
                            deviceId: (d as any).index,
                            actuateType: SupportedAction.Vibrate,
                            actuateVals,
                            duration,
                        });
                    }
                })]),
                centeredRow([this.createButton(readDeviceSchemaButtonLabel(deviceSchema, DEVICE_MENU_ACTIONS.stopDeviceCommand, DEVICE_MENU_LABELS.stopAll), () => {
                    const deviceList = IntifaceController.instance.getDevices();
                    for (const d of deviceList) {
                        MenuController.instance.onUserChoice.notify(DEVICE_MENU_ACTIONS.stopDeviceCommand, { deviceId: (d as any).index });
                    }
                })]),
            );
        } else {
            this.deviceListLabel = new Label(
                connected ? DEVICE_MENU_LABELS.noDevicesDetected : DEVICE_MENU_LABELS.connectToSeeDevices,
            );
            deviceRows.push(this.deviceListLabel);
        }

        const children: Widget[] = [];

        // 1) Intiface URL row
        if (this.isVrNodeVisible(deviceSchema, 'device-intiface-url-input')) {
            children.push(new Row([new Label(readDeviceSchemaTextAt(deviceSchema, 0, DEVICE_MENU_LABELS.intifaceUrl)), this.deviceUrlInput], {
                multiContainerAlignment: { main: FlexAlignment.Center, cross: Alignment.Center },
            }));
        }

        // 2) Top action button row (connect/environment/permit/revoke/hud)
        const topButtons: TextButton[] = [];
        if (this.isVrNodeVisible(deviceSchema, DEVICE_MENU_ACTIONS.connectToIntiface)) {
            topButtons.push(this.deviceConnectButton);
        }
        if (ic?.deviceControlMode === DeviceControlMode.Environment) {
            if (this.isVrNodeVisible(deviceSchema, DEVICE_MENU_ACTIONS.leaveEnvironment)) {
                topButtons.push(this.deviceLeaveEnvironmentButton);
            }
        } else if (this.isVrNodeVisible(deviceSchema, DEVICE_MENU_ACTIONS.enterEnvironment)) {
            topButtons.push(this.deviceEnvironmentButton);
        }
        if (this.isVrNodeVisible(deviceSchema, 'device-permit-remote-control')) {
            topButtons.push(this.devicePermitButton);
        }
        if (this.isVrNodeVisible(deviceSchema, 'device-revoke-remote-control')) {
            topButtons.push(this.deviceRevokeButton);
        }
        if (this.isVrNodeVisible(deviceSchema, DEVICE_MENU_ACTIONS.toggleHapticsHud)) {
            const hudEnabled = EventsClient.instance?.isHapticDisplayEnabled?.() ?? true;
            this.setButtonText(this.deviceHapticsHudButton, hudEnabled ? DEVICE_MENU_LABELS.hideHapticsHud : DEVICE_MENU_LABELS.showHapticsHud);
            topButtons.push(this.deviceHapticsHudButton);
        }
        if (topButtons.length > 0) {
            children.push(new Row(topButtons, {
                multiContainerAlignment: { main: FlexAlignment.Center, cross: Alignment.Center },
            }));
        }

        // Status block
        children.push(this.deviceStatusLabel, this.deviceModeLabel);

        // 3) Prosthetic section + buttons
        if (this.isVrNodeVisible(deviceSchema, DEVICE_MENU_ACTIONS.selectProstheticNone)
            || this.isVrNodeVisible(deviceSchema, DEVICE_MENU_ACTIONS.selectProstheticInny)
            || this.isVrNodeVisible(deviceSchema, DEVICE_MENU_ACTIONS.selectProstheticOuty)) {
            children.push(this.createSubheadingLabel(DEVICE_MENU_LABELS.prosthetic));
            const prostheticButtons: TextButton[] = [];
            if (this.isVrNodeVisible(deviceSchema, DEVICE_MENU_ACTIONS.selectProstheticNone)) {
                prostheticButtons.push(this.deviceProstheticNoneButton);
            }
            if (this.isVrNodeVisible(deviceSchema, DEVICE_MENU_ACTIONS.selectProstheticInny)) {
                prostheticButtons.push(this.deviceProstheticInnyButton);
            }
            if (this.isVrNodeVisible(deviceSchema, DEVICE_MENU_ACTIONS.selectProstheticOuty)) {
                prostheticButtons.push(this.deviceProstheticOutyButton);
            }
            if (prostheticButtons.length > 0) {
                children.push(new Row(prostheticButtons, {
                    multiContainerAlignment: { main: FlexAlignment.Center, cross: Alignment.Center },
                }));
            }
        }

        // 4) Device controls body (slider + duration + send/stop)
        children.push(...deviceRows);

        return this.createSpacedColumn(children, 8);
    }

    // ── Remote Device Connection ────────────────────────────────────────────

    private buildRemoteConnContent(): Widget {
        const schema = createRemoteDeviceConnMenuSchema();
        this.remoteConnKeyValue = new ValidatedVariable('', null);
        this.remoteConnKeyInput = new TextInput(this.remoteConnKeyValue);
        this.remoteConnStatusLabel = new Label('');
        const children: Widget[] = [this.createSubheadingLabel("Connect to Another User's Devices")];
        if (this.isVrNodeVisible(schema, 'remote-device-key-input')) {
            children.push(new Row([new Label("User's Key:"), this.remoteConnKeyInput], {
                multiContainerAlignment: { main: FlexAlignment.Center, cross: Alignment.Center },
            }));
        }
        if (this.isVrNodeVisible(schema, REMOTE_DEVICE_CONN_MENU_ACTIONS.connect)) {
            children.push(centeredRow([this.createButton('Connect', () => {
                const key = String(this.remoteConnKeyValue.value ?? '').trim();
                if (!key) {
                    this.remoteConnStatusLabel.text = "Enter a valid user's key.";
                    this.root?.update();
                    return;
                }

                const peerIdsBefore = new Set(IntifaceController.instance.getControllableUsers());

                this.remoteConnStatusLabel.text = `Connecting to: ${key}`;
                this.root?.update();

                void (async () => {
                    const connected = await IntifaceController.instance.ConnectRemoteDevices(key);
                    if (!connected) {
                        this.remoteConnStatusLabel.text = `Failed to connect to: ${key}`;
                        this.root?.update();
                        return;
                    }

                    const peerIdsAfter = IntifaceController.instance.getControllableUsers();
                    const newPeerId = peerIdsAfter.find((id) => !peerIdsBefore.has(id));
                    const targetPeerId = newPeerId ?? peerIdsAfter[peerIdsAfter.length - 1];

                    if (targetPeerId !== undefined) {
                        await this.switchVrSubmenu(Submenu.RemoteDeviceClone, targetPeerId);
                        return;
                    }

                    this.remoteConnStatusLabel.text = `Connected to: ${key}`;
                    this.root?.update();
                })();
            })]));
        }
        children.push(this.remoteConnStatusLabel);
        return this.createSpacedColumn(children, 8);
    }

    private buildRemoteCloneContent(): Widget {
        const peerId = this.currentRemotePeerId;
        const initialMode = IntifaceController.instance?.deviceControlMode ?? DeviceControlMode.Self;
        this.remoteCloneStatusLabel = new Label(peerId === null
            ? 'No remote peer selected.'
            : `Connected with peerID: ${peerId} | Control mode: ${initialMode}`);
        this.remoteCloneListLabel = new Label('');
        this.remoteCloneVibVariable = new Variable<number>(0);
        this.remoteCloneVibSlider = new Slider(this.remoteCloneVibVariable, 0, 100, {
            snapIncrement: 1,
            minHeight: this.vrSliderHeight,
            maxHeight: this.vrSliderHeight,
            sliderThickness: this.vrSliderHeight,
        });
        this.remoteCloneVibLabel = new Label('Vibration: 0%');
        this.remoteCloneDurationValue = new ValidatedVariable('', null);
        this.remoteCloneDurationInput = new TextInput(this.remoteCloneDurationValue);
        (this.remoteCloneDurationInput as unknown as { placeholder?: string }).placeholder = 'Duration sec (blank=persistent)';

        this.remoteCloneEnvironmentButton = this.createButton('Environment', () => {
            (IntifaceController.instance as any)._deviceControlMode = DeviceControlMode.Environment;
            IntifaceController.instance.onControlModeChanged.notify();
            void this.rebuildRoot();
        });
        this.remoteCloneLeaveEnvironmentButton = this.createButton('Leave Environment', () => {
            (IntifaceController.instance as any)._deviceControlMode = DeviceControlMode.Self;
            IntifaceController.instance.onControlModeChanged.notify();
            void this.rebuildRoot();
        });

        this.remoteCloneVibVariable.watch(() => {
            const val = Math.round(this.remoteCloneVibVariable.value);
            if (this.remoteCloneVibLabel) {
                this.remoteCloneVibLabel.text = `Vibration: ${val}%`;
            }
        });

        const children: Widget[] = [this.remoteCloneStatusLabel];
        if (peerId === null) {
            children.push(new Label('Select a remote user from the nav bar.'));
            return this.createSpacedColumn(children, 8);
        }

        const devices = Array.from(IntifaceController.instance.getDevices(peerId));
        const mode = IntifaceController.instance.deviceControlMode;

        if (mode === DeviceControlMode.Environment) {
            children.push(centeredRow([this.remoteCloneLeaveEnvironmentButton]));
        } else {
            children.push(centeredRow([this.remoteCloneEnvironmentButton]));
        }

        if (devices.length > 0) {
            this.remoteCloneListLabel.text = `Devices: ${devices.map((d: any) => String(d.name ?? 'Unknown')).join(', ')}`;
            children.push(this.remoteCloneListLabel, this.remoteCloneVibLabel);

            // In Environment mode the environment drives intensity, so keep this read-only.
            // In Self mode, expose the slider for direct command input.
            if (mode !== DeviceControlMode.Environment) {
                children.push(this.remoteCloneVibSlider);
            }

            if (mode !== DeviceControlMode.Environment) {
                children.push(
                    new Row([new Label('Duration:'), this.remoteCloneDurationInput], {
                        multiContainerAlignment: { main: FlexAlignment.Center, cross: Alignment.Center },
                    }),
                    centeredRow([this.createButton('Send Vibration', () => {
                        const percent = Math.max(0, Math.min(100, Math.round(this.remoteCloneVibVariable.value)));
                        const durationValue = String(this.remoteCloneDurationValue.value ?? '').trim();
                        const duration = durationValue !== '' ? durationValue : undefined;
                        const remoteDevices = Array.from(IntifaceController.instance.getDevices(peerId));
                        for (const d of remoteDevices) {
                            const deviceId = Number((d as any).index);
                            if (!Number.isFinite(deviceId)) {
                                continue;
                            }
                            const actuatorCount = IntifaceController.instance.getActuatorCount(d as any, SupportedAction.Vibrate);
                            const actuateVals: string[] = [];
                            for (let idx = 0; idx < Math.max(1, actuatorCount); idx++) {
                                const steps = IntifaceController.instance.getVibeSteps(d as any, idx) || 1;
                                const scaled = Math.round((percent / 100) * steps);
                                actuateVals.push(String(scaled));
                            }

                            MenuController.instance.onUserChoice.notify(DEVICE_MENU_ACTIONS.sendRemoteDeviceCommand, {
                                peerId,
                                deviceId,
                                actuateType: SupportedAction.Vibrate,
                                actuateVals,
                                duration,
                            });
                        }
                        this.remoteCloneStatusLabel.text = `Sent vibration at ${percent}%`;
                        this.root?.update();
                    })]),
                    centeredRow([this.createButton('Stop All', () => {
                        const remoteDevices = Array.from(IntifaceController.instance.getDevices(peerId));
                        for (const d of remoteDevices) {
                            const deviceId = Number((d as any).index);
                            if (!Number.isFinite(deviceId)) {
                                continue;
                            }
                            MenuController.instance.onUserChoice.notify(DEVICE_MENU_ACTIONS.stopRemoteDeviceCommand, {
                                peerId,
                                deviceId,
                            });
                        }
                    })]),
                );
            }
        } else {
            this.remoteCloneListLabel.text = 'No remote devices detected yet.';
            children.push(this.remoteCloneListLabel);
        }

        return this.createSpacedColumn(children, 8);
    }

    // ── Settings ────────────────────────────────────────────────────────────

    private getAudioTuningApi(): AudioTuningApi | null {
        const api = (window as any).wecloudAudioTuning as AudioTuningApi | undefined;
        if (!api || typeof api.get !== 'function' || typeof api.set !== 'function') {
            return null;
        }
        return api;
    }

    private getDefaultAudioTuningSettings(): RuntimeAudioTuningSettings {
        return {
            audioRoutingMode: 'split',
            echoCancellation: false,
            noiseSuppression: false,
            noiseSuppressionMode: 'default',
            broadcastDisableProcessing: false,
            autoGainControl: false,
            enableSenderBitrateCap: false,
            speechMaxBitrate: 64000,
            musicMaxBitrate: 128000,
            contentHintMode: 'auto',
        };
    }

    private getCurrentAudioTuningSettings(api: AudioTuningApi | null): RuntimeAudioTuningSettings {
        if (!api) {
            return this.getDefaultAudioTuningSettings();
        }

        try {
            const settings = api.get();
            return {
                ...this.getDefaultAudioTuningSettings(),
                ...settings,
            };
        } catch {
            return this.getDefaultAudioTuningSettings();
        }
    }

    private formatPersistedSettingsDebugText(): string {
        const lines = UserSettingsStore.getKnownPersistedSettings().map((entry) => {
            const value = entry.value === '' ? '<empty>' : entry.value;
            return `[${entry.storage}] ${entry.key} = ${value}`;
        });
        return lines.join('\n');
    }

    private refreshSettingsDebugText(): void {
        const text = this.formatPersistedSettingsDebugText();
        this.settingsDebugTextValue.value = text;
    }

    private clampAudioBitrate(value: number, min: number): number {
        if (!Number.isFinite(value)) {
            return min;
        }
        return Math.max(min, Math.min(512000, Math.round(value)));
    }

    private cycleSettingOption<T extends string>(current: T, options: readonly T[]): T {
        const idx = options.indexOf(current);
        if (idx < 0) {
            return options[0];
        }
        return options[(idx + 1) % options.length];
    }

    private buildSettingsContent(): Widget {
        const settingsSchema = createSettingsMenuSchema();
        const mode = UserSettingsStore.getRotationMode();
        const smoothSpeed = UserSettingsStore.getNumber(UserSettingsStore.Keys.SmoothRotationSpeed) ?? 90;
        const snapAngle = UserSettingsStore.getNumber(UserSettingsStore.Keys.SnapRotationAngle) ?? 45;
        const audioApi = this.getAudioTuningApi();
        const audio = this.getCurrentAudioTuningSettings(audioApi);
        const density = this.settingsCompactLayout
            ? {
                rowSpacing: 5,
                rootSpacing: 6,
                labelWidth: 220,
                titleFont: '700 22px "Orbitron", "Rajdhani", sans-serif',
                debugMinHeight: 130,
                debugMaxHeight: 200,
            }
            : {
                rowSpacing: 7,
                rootSpacing: 10,
                labelWidth: 260,
                titleFont: '700 24px "Orbitron", "Rajdhani", sans-serif',
                debugMinHeight: 180,
                debugMaxHeight: 260,
            };

        this.settingsRotationButton = this.createButton(
            mode === 'smooth'
                ? readSettingsSchemaButtonLabel(settingsSchema, SETTINGS_MENU_ACTIONS.toggleRotationMode, SETTINGS_MENU_LABELS.rotationModeSmooth)
                : formatRotationModeLabel('snap'),
            () => {
                const current = UserSettingsStore.getRotationMode();
                const next = current === 'smooth' ? 'snap' : 'smooth';
                UserSettingsStore.setString(UserSettingsStore.Keys.RotationMode, next);
                this.applyLiveRotationMode(next);
                this.setButtonText(this.settingsRotationButton, formatRotationModeLabel(next));
                this.settingsSpeedLabel.text = formatRotationSpeedLabel(next, this.settingsSpeedVariable.value);
                this.refreshSettingsDebugText();
            },
        );

        const isSmooth = mode === 'smooth';
        const speedInitial = isSmooth ? smoothSpeed : snapAngle;
        const speedMin = isSmooth ? 10 : 5;
        const speedMax = isSmooth ? 360 : 180;
        const speedSnap = isSmooth ? 5 : 5;
        this.settingsSpeedVariable = new Variable<number>(speedInitial);
        this.settingsSpeedSlider = new Slider(this.settingsSpeedVariable, speedMin, speedMax, {
            snapIncrement: speedSnap,
            minHeight: this.vrSliderHeight,
            maxHeight: this.vrSliderHeight,
            sliderThickness: this.vrSliderHeight,
        });
        this.settingsSpeedLabel = new Label(formatRotationSpeedLabel(isSmooth ? 'smooth' : 'snap', speedInitial));
        this.settingsSpeedVariable.watch(() => {
            const v = Math.round(this.settingsSpeedVariable.value);
            const m = UserSettingsStore.getRotationMode();
            if (m === 'smooth') {
                UserSettingsStore.setNumber(UserSettingsStore.Keys.SmoothRotationSpeed, v);
                this.applyLiveRotationValue('smooth', v);
                this.settingsSpeedLabel.text = formatRotationSpeedLabel('smooth', v);
            } else {
                UserSettingsStore.setNumber(UserSettingsStore.Keys.SnapRotationAngle, v);
                this.applyLiveRotationValue('snap', v);
                this.settingsSpeedLabel.text = formatRotationSpeedLabel('snap', v);
            }
            this.refreshSettingsDebugText();
        });

        const routingOptions: Array<AudioRoutingMode> = ['split', 'premix'];
        const hintOptions: Array<AudioHintMode> = ['auto', 'speech', 'music', 'none'];
        const noiseModeOptions: Array<NoiseSuppressionMode> = ['default', 'mild', 'aggressive'];

        this.settingsAudioRoutingButton = this.createButton(audio.audioRoutingMode, () => {
            if (!audioApi) {
                return;
            }
            const curr = this.getCurrentAudioTuningSettings(audioApi);
            const next = this.cycleSettingOption(curr.audioRoutingMode, routingOptions);
            audioApi.set({ audioRoutingMode: next });
            this.setButtonText(this.settingsAudioRoutingButton, next);
            this.refreshSettingsDebugText();
        });
        this.settingsContentHintButton = this.createButton(audio.contentHintMode, () => {
            if (!audioApi) {
                return;
            }
            const curr = this.getCurrentAudioTuningSettings(audioApi);
            const next = this.cycleSettingOption(curr.contentHintMode, hintOptions);
            audioApi.set({ contentHintMode: next });
            this.setButtonText(this.settingsContentHintButton, next);
            this.refreshSettingsDebugText();
        });
        this.settingsNoiseModeButton = this.createButton(audio.noiseSuppressionMode, () => {
            if (!audioApi) {
                return;
            }
            const curr = this.getCurrentAudioTuningSettings(audioApi);
            const next = this.cycleSettingOption(curr.noiseSuppressionMode, noiseModeOptions);
            audioApi.set({ noiseSuppressionMode: next });
            this.setButtonText(this.settingsNoiseModeButton, next);
            this.refreshSettingsDebugText();
        });

        this.settingsSpeechBitrateValue = new ValidatedVariable(String(audio.speechMaxBitrate), null);
        this.settingsSpeechBitrateInput = new TextInput(this.settingsSpeechBitrateValue, {
            inputFilter: (input: string) => /^\d*$/.test(input),
        });
        (this.settingsSpeechBitrateInput as unknown as { placeholder?: string }).placeholder = '16000-512000';

        this.settingsMusicBitrateValue = new ValidatedVariable(String(audio.musicMaxBitrate), null);
        this.settingsMusicBitrateInput = new TextInput(this.settingsMusicBitrateValue, {
            inputFilter: (input: string) => /^\d*$/.test(input),
        });
        (this.settingsMusicBitrateInput as unknown as { placeholder?: string }).placeholder = '32000-512000';

        this.settingsEchoVariable = new Variable<boolean>(!!audio.echoCancellation);
        this.settingsEchoCheckbox = this.createCheckbox(this.settingsEchoVariable);
        this.settingsNoiseVariable = new Variable<boolean>(!!audio.noiseSuppression);
        this.settingsNoiseCheckbox = this.createCheckbox(this.settingsNoiseVariable);
        this.settingsBroadcastDisableVariable = new Variable<boolean>(!!audio.broadcastDisableProcessing);
        this.settingsBroadcastDisableCheckbox = this.createCheckbox(this.settingsBroadcastDisableVariable);
        this.settingsAutoGainVariable = new Variable<boolean>(!!audio.autoGainControl);
        this.settingsAutoGainCheckbox = this.createCheckbox(this.settingsAutoGainVariable);
        this.settingsBitrateCapVariable = new Variable<boolean>(!!audio.enableSenderBitrateCap);
        this.settingsBitrateCapCheckbox = this.createCheckbox(this.settingsBitrateCapVariable);

        this.settingsEchoVariable.watch(() => {
            if (!audioApi) {
                return;
            }
            audioApi.set({ echoCancellation: !!this.settingsEchoVariable.value });
            this.refreshSettingsDebugText();
        });
        this.settingsNoiseVariable.watch(() => {
            if (!audioApi) {
                return;
            }
            audioApi.set({ noiseSuppression: !!this.settingsNoiseVariable.value });
            this.refreshSettingsDebugText();
        });
        this.settingsBroadcastDisableVariable.watch(() => {
            if (!audioApi) {
                return;
            }
            audioApi.set({ broadcastDisableProcessing: !!this.settingsBroadcastDisableVariable.value });
            this.refreshSettingsDebugText();
        });
        this.settingsAutoGainVariable.watch(() => {
            if (!audioApi) {
                return;
            }
            audioApi.set({ autoGainControl: !!this.settingsAutoGainVariable.value });
            this.refreshSettingsDebugText();
        });
        this.settingsBitrateCapVariable.watch(() => {
            if (!audioApi) {
                return;
            }
            audioApi.set({ enableSenderBitrateCap: !!this.settingsBitrateCapVariable.value });
            this.refreshSettingsDebugText();
        });

        const applyBitratesButton = this.createButton('Apply Bitrates', () => {
            if (!audioApi) {
                this.settingsDebugStatusLabel.text = 'Audio tuning API unavailable.';
                return;
            }

            const speechRaw = Number(String(this.settingsSpeechBitrateValue.value ?? '').trim());
            const musicRaw = Number(String(this.settingsMusicBitrateValue.value ?? '').trim());
            const speech = this.clampAudioBitrate(speechRaw, 16000);
            const music = this.clampAudioBitrate(musicRaw, 32000);

            audioApi.set({ speechMaxBitrate: speech, musicMaxBitrate: music });
            this.settingsSpeechBitrateValue.value = String(speech);
            this.settingsMusicBitrateValue.value = String(music);
            this.settingsDebugStatusLabel.text = 'Bitrates updated.';
            this.refreshSettingsDebugText();
        });

        this.settingsDebugTextValue = new ValidatedVariable(this.formatPersistedSettingsDebugText(), null);
        this.settingsDebugTextArea = new TextArea(this.settingsDebugTextValue, {
            wrapText: true,
            editingEnabled: false,
            scrollbarMode: ScrollbarMode.Layout,
            minHeight: density.debugMinHeight,
            maxHeight: density.debugMaxHeight,
            minWidth: 560,
            // Keep the last row of text clear of the horizontal scrollbar area.
            inputTextInnerPadding: { left: 12, right: 12, top: 8, bottom: 18 },
        });
        this.settingsDebugStatusLabel = new Label('');

        const debugCopyButton = this.createButton(SETTINGS_MENU_LABELS.webDebugCopy, () => {
            const textToCopy = String(this.settingsDebugTextValue.value ?? '');
            if (!textToCopy) {
                return;
            }

            if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                navigator.clipboard.writeText(textToCopy)
                    .then(() => {
                        this.settingsDebugStatusLabel.text = 'Copied debug settings.';
                        this.root?.update();
                    })
                    .catch(() => {
                        this.settingsDebugStatusLabel.text = 'Copy failed.';
                        this.root?.update();
                    });
                return;
            }

            this.settingsDebugStatusLabel.text = 'Clipboard API unavailable.';
        });
        const debugRefreshButton = this.createButton(SETTINGS_MENU_LABELS.webDebugRefresh, () => {
            this.refreshSettingsDebugText();
            this.settingsDebugStatusLabel.text = 'Debug settings refreshed.';
        });

        const leftAlignedRow = { multiContainerAlignment: { main: FlexAlignment.Start, cross: Alignment.Center } };
        const sectionTitleStyle = {
            bodyTextFont: density.titleFont,
            bodyTextFill: this.getMenuThemeColors().textFill,
        };
        const makeFieldLabel = (text: string): Label => new Label(text, {
            minWidth: density.labelWidth,
            maxWidth: density.labelWidth,
        });

        const rotationSectionChildren: Widget[] = [
            new Label(SETTINGS_MENU_LABELS.webRotationCardTitle, sectionTitleStyle),
        ];
        if (this.isVrNodeVisible(settingsSchema, SETTINGS_MENU_ACTIONS.toggleRotationMode)) {
            rotationSectionChildren.push(new Row([
                makeFieldLabel(SETTINGS_MENU_LABELS.webRotationModeLabel),
                this.settingsRotationButton,
            ], { ...leftAlignedRow }));
        }
        if (this.isVrNodeVisible(settingsSchema, SETTINGS_MENU_ACTIONS.adjustRotationSpeed)) {
            rotationSectionChildren.push(this.settingsSpeedLabel, this.settingsSpeedSlider);
        }

        const checkboxRows: Widget[] = [
            new Row([makeFieldLabel('Echo Cancellation'), this.settingsEchoCheckbox], { ...leftAlignedRow }),
            new Row([makeFieldLabel('Noise Suppression'), this.settingsNoiseCheckbox], { ...leftAlignedRow }),
            new Row([makeFieldLabel('Broadcast Disable Processing'), this.settingsBroadcastDisableCheckbox], { ...leftAlignedRow }),
            new Row([makeFieldLabel('Auto Gain Control'), this.settingsAutoGainCheckbox], { ...leftAlignedRow }),
            new Row([makeFieldLabel('Enable Sender Bitrate Cap'), this.settingsBitrateCapCheckbox], { ...leftAlignedRow }),
        ];

        const audioSectionChildren: Widget[] = [
            new Label(SETTINGS_MENU_LABELS.webAudioCardTitle, sectionTitleStyle),
            new Row([makeFieldLabel('Audio Routing Mode'), this.settingsAudioRoutingButton], { ...leftAlignedRow }),
            new Row([makeFieldLabel('Content Hint Mode'), this.settingsContentHintButton], { ...leftAlignedRow }),
            new Row([makeFieldLabel('Noise Suppression Mode'), this.settingsNoiseModeButton], { ...leftAlignedRow }),
            new Row([makeFieldLabel('Speech Max Bitrate'), this.settingsSpeechBitrateInput], { ...leftAlignedRow }),
            new Row([makeFieldLabel('Music Max Bitrate'), this.settingsMusicBitrateInput], { ...leftAlignedRow }),
            new Row([makeFieldLabel(''), applyBitratesButton], { ...leftAlignedRow }),
            ...checkboxRows,
        ];

        if (!audioApi) {
            audioSectionChildren.push(new Label('Audio tuning API unavailable in this runtime.'));
        }

        const debugSectionChildren: Widget[] = [
            new Label(SETTINGS_MENU_LABELS.webDebugTitle, sectionTitleStyle),
            this.settingsDebugTextArea,
            actionRow([debugCopyButton, debugRefreshButton]),
            this.settingsDebugStatusLabel,
        ];

        const children: Widget[] = [
            this.createSpacedColumn(rotationSectionChildren, density.rowSpacing),
            this.createSpacedColumn(audioSectionChildren, density.rowSpacing),
            new Label(readSettingsSchemaTextAt(settingsSchema, 3, SETTINGS_MENU_LABELS.helperText), {
            }),
            this.createSpacedColumn(debugSectionChildren, density.rowSpacing),
        ];

        this.refreshSettingsDebugText();
        return this.createSpacedColumn(children, density.rootSpacing);
    }

    // ── Account ─────────────────────────────────────────────────────────────

    private buildAccountContent(): Widget {
        const accountSchema = createAccountMenuSchema();
        this.accountUsernameLabel = new Label(ACCOUNT_MENU_LABELS.usernameLoading);
        this.accountTokenBalanceLabel = new Label(ACCOUNT_MENU_LABELS.tokenBalanceLoading);
        this.accountStatusLabel = new Label('');
        this.accountPasswordStatusLabel = new Label('');

        this.accountDisplayNameValue = new ValidatedVariable('', null);
        this.accountDisplayNameInput = new TextInput(this.accountDisplayNameValue);
        (this.accountDisplayNameInput as unknown as { placeholder?: string }).placeholder = readAccountSchemaInputPlaceholder(
            accountSchema,
            'account-display-name-input',
            ACCOUNT_MENU_LABELS.displayNamePlaceholder,
        );

        this.accountEmailValue = new ValidatedVariable('', null);
        this.accountEmailInput = new TextInput(this.accountEmailValue);
        (this.accountEmailInput as unknown as { placeholder?: string }).placeholder = readAccountSchemaInputPlaceholder(
            accountSchema,
            'account-email-input',
            ACCOUNT_MENU_LABELS.emailPlaceholder,
        );

        this.accountCurrentPasswordValue = new ValidatedVariable('', null);
        this.accountCurrentPasswordInput = new TextInput(this.accountCurrentPasswordValue, { hideText: true });
        (this.accountCurrentPasswordInput as unknown as { placeholder?: string }).placeholder = readAccountSchemaInputPlaceholder(
            accountSchema,
            'account-current-password-input',
            ACCOUNT_MENU_LABELS.currentPasswordPlaceholder,
        );

        this.accountNewPasswordValue = new ValidatedVariable('', null);
        this.accountNewPasswordInput = new TextInput(this.accountNewPasswordValue, { hideText: true });
        (this.accountNewPasswordInput as unknown as { placeholder?: string }).placeholder = readAccountSchemaInputPlaceholder(
            accountSchema,
            'account-new-password-input',
            ACCOUNT_MENU_LABELS.newPasswordPlaceholder,
        );

        this.accountConfirmPasswordValue = new ValidatedVariable('', null);
        this.accountConfirmPasswordInput = new TextInput(this.accountConfirmPasswordValue, { hideText: true });
        (this.accountConfirmPasswordInput as unknown as { placeholder?: string }).placeholder = readAccountSchemaInputPlaceholder(
            accountSchema,
            'account-confirm-password-input',
            ACCOUNT_MENU_LABELS.confirmPasswordPlaceholder,
        );

        const accountChildren: Widget[] = [
            this.createSubheadingLabel('Profile'),
            this.accountUsernameLabel,
            this.accountTokenBalanceLabel,
        ];
        if (this.isVrNodeVisible(accountSchema, 'account-display-name-input')) {
            accountChildren.push(this.createLabeledControlRow(
                `${ACCOUNT_MENU_LABELS.displayName}:`,
                this.accountDisplayNameInput,
                230,
            ));
        }
        if (this.isVrNodeVisible(accountSchema, 'account-email-input')) {
            accountChildren.push(this.createLabeledControlRow(
                `${ACCOUNT_MENU_LABELS.email}:`,
                this.accountEmailInput,
                230,
            ));
        }
        if (this.isVrNodeVisible(accountSchema, ACCOUNT_MENU_ACTIONS.saveProfile)) {
            accountChildren.push(actionRow([this.createButton(
                readAccountSchemaButtonLabel(accountSchema, ACCOUNT_MENU_ACTIONS.saveProfile, ACCOUNT_MENU_LABELS.saveChanges),
                () => { void this.saveProfile(); },
            )]));
        }
        accountChildren.push(this.accountStatusLabel);

        if (this.isVrNodeVisible(accountSchema, 'account-current-password-input')) {
            accountChildren.push(this.createSubheadingLabel('Change Password'));
            accountChildren.push(this.createLabeledControlRow(
                `${ACCOUNT_MENU_LABELS.currentPassword}:`,
                this.accountCurrentPasswordInput,
                230,
            ));
        }
        if (this.isVrNodeVisible(accountSchema, 'account-new-password-input')) {
            accountChildren.push(this.createLabeledControlRow(
                `${ACCOUNT_MENU_LABELS.newPassword}:`,
                this.accountNewPasswordInput,
                230,
            ));
        }
        if (this.isVrNodeVisible(accountSchema, 'account-confirm-password-input')) {
            accountChildren.push(this.createLabeledControlRow(
                `${ACCOUNT_MENU_LABELS.confirmPassword}:`,
                this.accountConfirmPasswordInput,
                230,
            ));
        }
        if (this.isVrNodeVisible(accountSchema, ACCOUNT_MENU_ACTIONS.changePassword)) {
            accountChildren.push(actionRow([this.createButton(
                readAccountSchemaButtonLabel(accountSchema, ACCOUNT_MENU_ACTIONS.changePassword, ACCOUNT_MENU_LABELS.changePassword),
                () => { void this.changePassword(); },
            )]));
        }
        accountChildren.push(this.accountPasswordStatusLabel);

        return this.createSpacedColumn(accountChildren, 8);
    }

    // ── Rooms ────────────────────────────────────────────────────────────────

    private buildRoomsContent(): Widget {
        const roomsSchema = createRoomsMenuSchema();
        const ec = EventsClient.instance;
        const currentRoom = (ec as any)?.getCurrentRoom?.() ?? (ec as any)?.room ?? 'unknown';
        const selectedRoom = this.roomsData.find((room) => room.name === this.roomsSelectedRoomName) ?? null;
        const roomSelected = !!selectedRoom;
        const showJoinPassword = !!selectedRoom?.isPrivate;
        const roomsStatusText = this.roomsLoadState === 'error'
            ? ROOMS_MENU_LABELS.failedToLoadRooms
            : this.roomsLoadState === 'loaded'
                ? (this.roomsData.length === 0 ? ROOMS_MENU_LABELS.noActiveRooms : `${this.roomsData.length} room(s) available.`)
                : ROOMS_MENU_LABELS.loadingRooms;
        this.roomsCurrentLabel = new Label(`${ROOMS_MENU_LABELS.currentRoomPrefix} ${String(currentRoom)}`);
        this.roomsStatusLabel = new Label(roomsStatusText);
        this.roomsJoinPasswordValue = new ValidatedVariable(this.roomsJoinPasswordDraft, null);
        this.roomsJoinPasswordInput = new TextInput(this.roomsJoinPasswordValue);
        (this.roomsJoinPasswordInput as unknown as { placeholder?: string }).placeholder = readRoomsSchemaInputPlaceholder(
            roomsSchema,
            'rooms-password-input',
            ROOMS_MENU_LABELS.passwordPlaceholder,
        );
        this.roomsCreateNameValue = new ValidatedVariable(this.roomsCreateNameDraft, null);
        this.roomsCreateNameInput = new TextInput(this.roomsCreateNameValue);
        (this.roomsCreateNameInput as unknown as { placeholder?: string }).placeholder = readRoomsSchemaInputPlaceholder(
            roomsSchema,
            'rooms-name-input',
            ROOMS_MENU_LABELS.roomNamePlaceholder,
        );
        this.roomsCreatePasswordValue = new ValidatedVariable(this.roomsCreatePasswordDraft, null);
        this.roomsCreatePasswordInput = new TextInput(this.roomsCreatePasswordValue);
        (this.roomsCreatePasswordInput as unknown as { placeholder?: string }).placeholder = readRoomsSchemaInputPlaceholder(
            roomsSchema,
            'rooms-password-input',
            ROOMS_MENU_LABELS.passwordPlaceholder,
        );
        this.roomsJoinButton = this.createButton(
            roomSelected ? 'JOIN ROOM' : 'SELECT A ROOM TO JOIN',
            () => { void this.joinSelectedRoom(); },
        );
        this.roomsCreateButton = this.createButton(
            readRoomsSchemaButtonLabel(roomsSchema, ROOMS_MENU_ACTIONS.createRoom, ROOMS_MENU_LABELS.createJoinRoom),
            () => { void this.createOrJoinRoomFromCreateSection(); },
        );
        this.roomsRefreshButton = this.createButton(
            readRoomsSchemaButtonLabel(roomsSchema, ROOMS_MENU_ACTIONS.refreshRooms, ROOMS_MENU_LABELS.refreshList),
            () => {
                this.roomsLoadState = 'idle';
                void this.loadRoomsData();
            },
        );

        (this.roomsJoinButton as unknown as { enabled?: boolean }).enabled = roomSelected;

        this.roomsJoinPasswordValue.watch(() => {
            this.roomsJoinPasswordDraft = String(this.roomsJoinPasswordValue.value ?? '');
        });
        this.roomsCreateNameValue.watch(() => {
            this.roomsCreateNameDraft = String(this.roomsCreateNameValue.value ?? '');
        });
        this.roomsCreatePasswordValue.watch(() => {
            this.roomsCreatePasswordDraft = String(this.roomsCreatePasswordValue.value ?? '');
        });
        const roomsChildren: Widget[] = [this.createSubheadingLabel('Active Rooms')];
        const rowHeight = Math.max(28, Math.min(64, this.vrMenuTableRowHeight));
        const tableScrollbarGap = Math.max(0, Math.min(40, Math.round(this.vrMenuTableScrollbarGap)));
        const roomsListChildren: Widget[] = [];

        if (this.roomsLoadState === 'loaded') {
            if (this.roomsData.length === 0) {
                roomsListChildren.push(new Label(ROOMS_MENU_LABELS.noActiveRooms));
            } else {
                roomsListChildren.push(this.createTableHeaderRow([
                    { label: 'Room Name', width: 260 },
                    { label: 'Users', width: 80 },
                    { label: 'Access', width: 120 },
                ], rowHeight, tableScrollbarGap));

                for (const room of this.roomsData) {
                    const isSelected = this.roomsSelectedRoomName === room.name;
                    const isCurrent = room.name === currentRoom;
                    const onSelect = () => {
                        this.roomsSelectedRoomName = room.name;
                        if (!room.isPrivate) {
                            this.roomsJoinPasswordDraft = '';
                            this.roomsJoinPasswordValue.value = '';
                        }
                        if (this.roomsStatusLabel) {
                            this.roomsStatusLabel.text = `Selected room: ${room.name}`;
                        }
                        void this.rebuildRoot();
                    };

                    roomsListChildren.push(this.createTableDataRow([
                        this.createTableCellButton(`${isSelected ? '▶ ' : ''}${room.name}${isCurrent ? ' (current)' : ''}`, onSelect, 260, isSelected),
                        this.createTableCellButton(String(room.userCount ?? 0), onSelect, 80, isSelected),
                        this.createTableCellButton(room.isPrivate ? 'private' : 'public', onSelect, 120, isSelected),
                    ], rowHeight, tableScrollbarGap));
                }
            }
        } else if (this.roomsLoadState === 'loading') {
            roomsListChildren.push(new Label(ROOMS_MENU_LABELS.loadingRooms));
        } else if (this.roomsLoadState === 'error') {
            roomsListChildren.push(new Label(ROOMS_MENU_LABELS.failedToLoadRooms));
        }

        // Show vertical scrollbar only when rows exceed the visible window.
        // 4 entries + 1 header row fit without scrolling.
        const visibleRowsBeforeScroll = 5;
        const roomsListViewportHeight = Math.max(130, Math.min(520, rowHeight * visibleRowsBeforeScroll));
        const roomsListViewportWidth = Math.max(460, Math.min(760, 260 + 80 + 120 + tableScrollbarGap + 28));
        roomsChildren.push(centeredRow([
            this.createScrollableListViewport(roomsListChildren, roomsListViewportHeight, ScrollbarMode.Overlay, roomsListViewportWidth),
        ]));

        roomsChildren.push(this.createSubheadingLabel(roomSelected ? 'Join Room' : 'Select a room to join'));
        if (showJoinPassword && this.isVrNodeVisible(roomsSchema, 'rooms-password-input')) {
            roomsChildren.push(this.createLabeledControlRow(
                ROOMS_MENU_LABELS.roomPassword,
                this.roomsJoinPasswordInput,
                190,
            ));
        }
        if (this.isVrNodeVisible(roomsSchema, ROOMS_MENU_ACTIONS.joinRoom)) {
            roomsChildren.push(actionRow([this.roomsJoinButton]));
        }
        if (this.isVrNodeVisible(roomsSchema, ROOMS_MENU_ACTIONS.refreshRooms)) {
            roomsChildren.push(actionRow([this.roomsRefreshButton]));
        }

        roomsChildren.push(this.createSubheadingLabel(ROOMS_MENU_LABELS.createNewRoom));
        if (this.isVrNodeVisible(roomsSchema, 'rooms-name-input')) {
            roomsChildren.push(this.createLabeledControlRow(
                ROOMS_MENU_LABELS.roomName,
                this.roomsCreateNameInput,
                190,
            ));
        }
        if (this.isVrNodeVisible(roomsSchema, 'rooms-password-input')) {
            roomsChildren.push(this.createLabeledControlRow(
                ROOMS_MENU_LABELS.createPasswordLabel,
                this.roomsCreatePasswordInput,
                190,
            ));
        }
        if (this.isVrNodeVisible(roomsSchema, ROOMS_MENU_ACTIONS.createRoom)) {
            roomsChildren.push(actionRow([this.roomsCreateButton]));
        }

        // Keep status feedback, but avoid the top summary lines the table already covers.
        roomsChildren.push(this.roomsStatusLabel);
        return this.createSpacedColumn(roomsChildren, 8);
    }

    // ── Entertainers ────────────────────────────────────────────────────────

    private buildEntertainersContent(): Widget {
        const entertainersSchema = createEntertainersMenuSchema();
        const statusText = this.entertainersLoadState === 'error'
            ? 'Failed to load entertainers.'
            : this.entertainersLoadState === 'loaded'
                ? (this.entertainersData.length === 0 ? ENTERTAINERS_MENU_LABELS.noEntertainers : `${this.entertainersData.length} entertainer(s) available.`)
                : ENTERTAINERS_MENU_LABELS.loading;

        this.entertainersStatusLabel = new Label(statusText);
        this.entertainersMyBalanceLabel = new Label(`${ENTERTAINERS_MENU_LABELS.yourBalancePrefix} ${this.entertainersMyBalance}`);
        this.entertainersSelectedLabel = new Label('Selected: -');

        this.entertainersTipAmountValue = new ValidatedVariable('', null);
        this.entertainersTipAmountInput = new TextInput(this.entertainersTipAmountValue);
        (this.entertainersTipAmountInput as unknown as { placeholder?: string }).placeholder = readEntertainersSchemaInputPlaceholder(
            entertainersSchema,
            'entertainers-tip-amount-input',
            ENTERTAINERS_MENU_LABELS.tipAmountPlaceholder,
        );

        this.entertainersTipButton = this.createButton(
            readEntertainersSchemaButtonLabel(entertainersSchema, ENTERTAINERS_MENU_ACTIONS.sendTip, ENTERTAINERS_MENU_LABELS.sendTip),
            () => { void this.sendEntertainerTip(); },
        );
        this.entertainersRefreshButton = this.createButton(
            readEntertainersSchemaButtonLabel(entertainersSchema, ENTERTAINERS_MENU_ACTIONS.refreshList, ENTERTAINERS_MENU_LABELS.refreshList),
            () => {
                this.entertainersLoadState = 'idle';
                void this.loadEntertainersData();
            },
        );

        (this.entertainersTipButton as unknown as { enabled?: boolean }).enabled = !!this.entertainersSelectedUserId;

        const rowHeight = Math.max(28, Math.min(64, this.vrMenuTableRowHeight));
        const tableScrollbarGap = Math.max(0, Math.min(40, Math.round(this.vrMenuTableScrollbarGap)));

        const listChildren: Widget[] = [];
        if (this.entertainersLoadState === 'loaded') {
            if (this.entertainersData.length === 0) {
                listChildren.push(new Label(ENTERTAINERS_MENU_LABELS.noEntertainers));
            } else {
                listChildren.push(this.createTableHeaderRow([
                    { label: 'Username', width: 200 },
                    { label: 'Display Name', width: 200 },
                ], rowHeight, tableScrollbarGap));

                for (const entertainer of this.entertainersData) {
                    const isSelected = this.entertainersSelectedUserId === entertainer.id;
                    const onSelect = () => {
                        this.entertainersSelectedUserId = entertainer.id;
                        const displayName = entertainer.display_name || entertainer.username;
                        this.entertainersSelectedLabel.text = `Selected: ${displayName}`;
                        (this.entertainersTipButton as unknown as { enabled?: boolean }).enabled = true;
                        this.entertainersStatusLabel.text = `Selected entertainer: ${displayName}`;
                        void this.rebuildRoot();
                    };

                    listChildren.push(this.createTableDataRow([
                        this.createTableCellButton(`${isSelected ? '▶ ' : ''}${entertainer.username}`, onSelect, 200, isSelected),
                        this.createTableCellButton(entertainer.display_name ?? '—', onSelect, 200, isSelected),
                    ], rowHeight, tableScrollbarGap));
                }
            }
        } else if (this.entertainersLoadState === 'loading') {
            listChildren.push(new Label(ENTERTAINERS_MENU_LABELS.loading));
        } else if (this.entertainersLoadState === 'error') {
            listChildren.push(new Label('Failed to load entertainers.'));
        }

        const selectedRow = this.entertainersData.find((entry) => entry.id === this.entertainersSelectedUserId);
        if (selectedRow) {
            this.entertainersSelectedLabel.text = `Selected: ${selectedRow.display_name || selectedRow.username}`;
        }

        const children: Widget[] = [
            this.createSubheadingLabel('Available Entertainers'),
            this.entertainersMyBalanceLabel,
            this.entertainersSelectedLabel,
            this.createScrollableListViewport(listChildren, Math.max(140, Math.min(520, rowHeight * 8)), ScrollbarMode.Layout),
            this.createLabeledControlRow(ENTERTAINERS_MENU_LABELS.tipAmount, this.entertainersTipAmountInput, 220),
            actionRow([this.entertainersTipButton, this.entertainersRefreshButton]),
            this.entertainersStatusLabel,
        ];

        return this.createSpacedColumn(children, 8);
    }

    // ── User Admin ──────────────────────────────────────────────────────────

    private buildUserAdminContent(): Widget {
        const userAdminSchema = createUserAdminMenuSchema();
        const statusText = this.userAdminLoadState === 'error'
            ? USER_ADMIN_MENU_LABELS.failedToLoad
            : this.userAdminLoadState === 'loaded'
                ? `${this.userAdminData.length} user(s)`
                : readUserAdminSchemaTextAt(userAdminSchema, 0, USER_ADMIN_MENU_LABELS.loadingUsers);
        this.userAdminStatusLabel = new Label(statusText);
        this.userAdminPasswordStatusLabel = new Label('');
        this.userAdminUsernameValue = new ValidatedVariable(this.userAdminUsernameDraft, null);
        this.userAdminUsernameInput = new TextInput(this.userAdminUsernameValue);
        (this.userAdminUsernameInput as unknown as { placeholder?: string }).placeholder = readUserAdminSchemaInputPlaceholder(
            userAdminSchema,
            'user-admin-username-input',
            USER_ADMIN_MENU_LABELS.usernamePlaceholder,
        );
        this.userAdminDisplayNameValue = new ValidatedVariable(this.userAdminDisplayNameDraft, null);
        this.userAdminDisplayNameInput = new TextInput(this.userAdminDisplayNameValue);
        (this.userAdminDisplayNameInput as unknown as { placeholder?: string }).placeholder = readUserAdminSchemaInputPlaceholder(
            userAdminSchema,
            'user-admin-display-name-input',
            USER_ADMIN_MENU_LABELS.displayNamePlaceholder,
        );
        this.userAdminPasswordValue = new ValidatedVariable(this.userAdminPasswordDraft, null);
        this.userAdminPasswordInput = new TextInput(this.userAdminPasswordValue);
        (this.userAdminPasswordInput as unknown as { placeholder?: string }).placeholder = readUserAdminSchemaInputPlaceholder(
            userAdminSchema,
            'user-admin-password-input',
            USER_ADMIN_MENU_LABELS.passwordMinPlaceholder,
        );
        this.userAdminNewPasswordValue = new ValidatedVariable(this.userAdminNewPasswordDraft, null);
        this.userAdminNewPasswordInput = new TextInput(this.userAdminNewPasswordValue, { hideText: true });
        (this.userAdminNewPasswordInput as unknown as { placeholder?: string }).placeholder = USER_ADMIN_MENU_LABELS.passwordMinPlaceholder;
        this.userAdminIsAdminVariable = new Variable<boolean>(this.userAdminIsAdminDraft);
        this.userAdminIsAdminCheckbox = this.createCheckbox(this.userAdminIsAdminVariable);
        this.userAdminIsEntertainerVariable = new Variable<boolean>(this.userAdminIsEntertainerDraft);
        this.userAdminIsEntertainerCheckbox = this.createCheckbox(this.userAdminIsEntertainerVariable);
        this.userAdminDepositAmountValue = new ValidatedVariable('', null);
        this.userAdminDepositAmountInput = new TextInput(this.userAdminDepositAmountValue);
        (this.userAdminDepositAmountInput as unknown as { placeholder?: string }).placeholder = readUserAdminSchemaInputPlaceholder(
            userAdminSchema,
            'user-admin-deposit-amount-input',
            USER_ADMIN_MENU_LABELS.depositAmountPlaceholder,
        );
        this.userAdminTokenBalanceLabel = new Label('Selected User Token Balance: -');
        this.userAdminUsernameValue.watch(() => {
            this.userAdminUsernameDraft = String(this.userAdminUsernameValue.value ?? '');
        });
        this.userAdminDisplayNameValue.watch(() => {
            this.userAdminDisplayNameDraft = String(this.userAdminDisplayNameValue.value ?? '');
        });
        this.userAdminPasswordValue.watch(() => {
            this.userAdminPasswordDraft = String(this.userAdminPasswordValue.value ?? '');
        });
        this.userAdminNewPasswordValue.watch(() => {
            this.userAdminNewPasswordDraft = String(this.userAdminNewPasswordValue.value ?? '');
        });
        this.userAdminIsAdminVariable.watch(() => {
            this.userAdminIsAdminDraft = !!this.userAdminIsAdminVariable.value;
        });
        this.userAdminIsEntertainerVariable.watch(() => {
            this.userAdminIsEntertainerDraft = !!this.userAdminIsEntertainerVariable.value;
        });

        if (this.userAdminSelectedUserId) {
            this.userAdminTokenBalanceLabel.text = `Selected User Token Balance: ${this.userAdminSelectedTokenBalance}`;
        }

        const tableRowHeight = Math.max(28, Math.min(64, this.vrMenuTableRowHeight));
        const tableScrollbarGap = Math.max(0, Math.min(40, Math.round(this.vrMenuTableScrollbarGap)));

        this.userAdminUpsertButton = this.createButton(
            this.userAdminSelectedUserId
                ? USER_ADMIN_MENU_LABELS.updateSelected
                : readUserAdminSchemaButtonLabel(userAdminSchema, USER_ADMIN_MENU_ACTIONS.createUser, USER_ADMIN_MENU_LABELS.createUser),
            () => {
                if (this.userAdminSelectedUserId) {
                    void this.updateSelectedAdminUser();
                } else {
                    void this.createAdminUser();
                }
            },
        );
        this.userAdminRefreshButton = this.createButton(
            readUserAdminSchemaButtonLabel(userAdminSchema, USER_ADMIN_MENU_ACTIONS.refreshList, USER_ADMIN_MENU_LABELS.refreshList),
            () => {
                this.userAdminLoadState = 'idle';
                void this.loadUserAdminData();
            },
        );
        this.userAdminSetPasswordButton = this.createButton(
            readUserAdminSchemaButtonLabel(userAdminSchema, USER_ADMIN_MENU_ACTIONS.setPassword, USER_ADMIN_MENU_LABELS.setPassword),
            () => { void this.setSelectedAdminUserPassword(); },
        );
        this.userAdminDepositButton = this.createButton(
            readUserAdminSchemaButtonLabel(userAdminSchema, USER_ADMIN_MENU_ACTIONS.depositTokens, USER_ADMIN_MENU_LABELS.depositTokens),
            () => { void this.depositTokensToSelectedAdminUser(); },
        );

        if (!this.userAdminSelectedUserId) {
            (this.userAdminSetPasswordButton as unknown as { enabled?: boolean }).enabled = false;
            (this.userAdminDepositButton as unknown as { enabled?: boolean }).enabled = false;
        }

        const userAdminFieldWidth = 220;
        const actionButtons: TextButton[] = [this.userAdminUpsertButton];
        if (this.userAdminSelectedUserId) {
            actionButtons.push(this.createButton(USER_ADMIN_MENU_LABELS.clearForm, () => {
                this.clearUserAdminSelection();
                void this.rebuildRoot();
            }));
        }

        const formChildren: Widget[] = [this.createSubheadingLabel('User Form')];
        if (this.isVrNodeVisible(userAdminSchema, 'user-admin-username-input')) {
            formChildren.push(this.createLabeledControlRow(
                USER_ADMIN_MENU_LABELS.username,
                this.userAdminUsernameInput,
                userAdminFieldWidth,
            ));
        }
        if (this.isVrNodeVisible(userAdminSchema, 'user-admin-display-name-input')) {
            formChildren.push(this.createLabeledControlRow(
                USER_ADMIN_MENU_LABELS.displayName,
                this.userAdminDisplayNameInput,
                userAdminFieldWidth,
            ));
        }
        if (!this.userAdminSelectedUserId && this.isVrNodeVisible(userAdminSchema, 'user-admin-password-input')) {
            formChildren.push(this.createLabeledControlRow(
                'Password (new user only)',
                this.userAdminPasswordInput,
                userAdminFieldWidth,
            ));
        }
        formChildren.push(this.createLabeledControlRow(
            'User Administration Privilege',
            this.userAdminIsAdminCheckbox,
            userAdminFieldWidth,
        ));
        formChildren.push(this.createLabeledControlRow(
            USER_ADMIN_MENU_LABELS.isEntertainer,
            this.userAdminIsEntertainerCheckbox,
            userAdminFieldWidth,
        ));
        formChildren.push(this.userAdminTokenBalanceLabel);
        formChildren.push(actionRow(actionButtons), this.userAdminStatusLabel);
        const formSection = this.createSpacedColumn(formChildren, 8);

        const existingChildren: Widget[] = [this.createSubheadingLabel('Existing Users')];
        const existingListChildren: Widget[] = [];
        if (this.userAdminLoadState === 'loaded') {
            if (this.userAdminData.length === 0) {
                existingListChildren.push(new Label('No users found.'));
            } else {
                existingListChildren.push(this.createTableHeaderRow([
                    { label: 'Username', width: 180 },
                    { label: 'Display Name', width: 180 },
                    { label: 'Role', width: 70 },
                ], tableRowHeight, tableScrollbarGap));

                for (const user of this.userAdminData) {
                    const isSelected = this.userAdminSelectedUserId === user.id;
                    const onSelect = () => {
                        this.userAdminSelectedUserId = user.id;
                        this.userAdminUsernameDraft = user.username;
                        this.userAdminDisplayNameDraft = user.display_name ?? '';
                        this.userAdminIsAdminDraft = !!user.is_user_admin;
                        this.userAdminIsEntertainerDraft = !!user.is_entertainer;
                        this.userAdminSelectedTokenBalance = Number(user.token_balance ?? 0);
                        this.userAdminUsernameValue.value = user.username;
                        this.userAdminDisplayNameValue.value = user.display_name ?? '';
                        this.userAdminIsAdminVariable.value = !!user.is_user_admin;
                        this.userAdminIsEntertainerVariable.value = !!user.is_entertainer;
                        this.userAdminTokenBalanceLabel.text = `Selected User Token Balance: ${this.userAdminSelectedTokenBalance}`;
                        if (this.userAdminStatusLabel) {
                            this.userAdminStatusLabel.text = `Selected user: ${user.username}`;
                        }
                        void this.rebuildRoot();
                    };

                    existingListChildren.push(this.createTableDataRow([
                        this.createTableCellButton(`${isSelected ? '▶ ' : ''}${user.username}`, onSelect, 180, isSelected),
                        this.createTableCellButton(user.display_name ?? '—', onSelect, 180, isSelected),
                        this.createTableCellButton(
                            `${user.is_user_admin ? 'admin' : 'user'}${user.is_entertainer ? ' / ent' : ''}`,
                            onSelect,
                            70,
                            isSelected,
                        ),
                    ], tableRowHeight, tableScrollbarGap));
                }
            }
        } else if (this.userAdminLoadState === 'loading') {
            existingListChildren.push(new Label(USER_ADMIN_MENU_LABELS.loadingUsers));
        } else if (this.userAdminLoadState === 'error') {
            existingListChildren.push(new Label(USER_ADMIN_MENU_LABELS.failedToLoad));
        }

        const userListViewportHeight = Math.max(140, Math.min(520, tableRowHeight * 9));
        existingChildren.push(this.createScrollableListViewport(existingListChildren, userListViewportHeight, ScrollbarMode.Layout));

        existingChildren.push(actionRow([this.userAdminRefreshButton]));
        const existingSection = this.createSpacedColumn(existingChildren, 8);

        const passwordChildren: Widget[] = [
            this.createSubheadingLabel('Set Selected User Password'),
            this.createLabeledControlRow('New Password', this.userAdminNewPasswordInput, userAdminFieldWidth),
            actionRow([this.userAdminSetPasswordButton]),
            this.createLabeledControlRow(
                'One-Time Token Deposit',
                this.userAdminDepositAmountInput,
                userAdminFieldWidth,
            ),
            actionRow([this.userAdminDepositButton]),
            this.userAdminPasswordStatusLabel,
        ];
        const passwordSection = this.createSpacedColumn(passwordChildren, 8);

        return this.createSpacedColumn([
            new Row([formSection, existingSection], {
                multiContainerAlignment: { main: FlexAlignment.Center, cross: Alignment.Start },
                multiContainerSpacing: 12,
            }),
            passwordSection,
        ], 8);
    }

    // ── Visibility control ───────────────────────────────────────────────────

    /**
     * Make the VR menu panel visible and interactive.
     * Called by MenuController when entering VR menu mode.
     */
    showMenu(): void {
        this._menuVisible = true;
        this.syncSharedMediaPreviewMeshVisibility();
        this.syncAvatarPreviewVisibility();
        void this.refreshUserAdminVrVisibility();
        const materialCandidate = (this as unknown as { material?: { pipeline?: string } }).material;
        console.warn(`[${MODULE}] showMenu() pipeline=${materialCandidate?.pipeline ?? 'unknown'} uniform=${this.textureUniformName || '<auto>'}`);
        if (this.root) {
            this.root.enabled = true;
            // Apply visibility change immediately on the same frame.
            this.root.update();
            this.bindRootTextureToMaterial();
        } else {
            // Root not ready yet (async init); wantEnabled tracks intent.
            this.wantEnabled = this._menuVisible;
        }
        Logger.debug(MODULE, 'Menu shown');
    }

    isMenuVisible(): boolean {
        return this._menuVisible;
    }

    /**
     * Hide the VR menu panel and disable cursor interaction.
     * Called by MenuController when closing the menu in VR.
     */
    hideMenu(): void {
        this._menuVisible = false;
        this.syncSharedMediaPreviewMeshVisibility();
        this.syncAvatarPreviewVisibility();
        if (this.root) {
            this.root.enabled = false;
        } else {
            this.wantEnabled = this._menuVisible;
        }
        Logger.debug(MODULE, 'Menu hidden');
    }

    override onActivate(): void {
        // Prevent BaseLazyWidgetsComponent from auto-showing this UI root.
        this.wantEnabled = this._menuVisible;
        if (this.root) {
            this.root.enabled = this._menuVisible;
            if (this._menuVisible) {
                this.root.update();
            }
        }
    }

    override onRootReady(root: any): void {
        // Root creation is async; enforce hidden-by-default once ready.
        root.enabled = this._menuVisible;
        this.restoreOffscreenCanvasPatch();

        const colliderGroup = root?.getCollider?.()?.group;
        console.warn(`[${MODULE}] root ready interaction: colliderGroup=${String(colliderGroup ?? 'n/a')} pointerDriver=true`);
        if (this._menuVisible) {
            root.update();
            this.bindRootTextureToMaterial();
        }
        // Kick off any async data loading for the current submenu
        if (this.activeMenuMode === 'submenu') {
            void this.loadSubmenuData(this.currentVrSubmenu);
        }
    }

    override onDestroy(): void {
        this.restoreOffscreenCanvasPatch();
        super.onDestroy();
    }

    override beforeWidgetUpdate(): boolean | void {
        this.syncMenuSlideToKeyboard();
        this.syncSharedMediaPreviewMeshVisibility();

        if (!this._menuVisible) {
            return false;
        }

        if (this.activeMenuMode === 'preauth') {
            if (this.preAuthMenuState?.stage === 'login') {
                EventsClient.instanceOrNull?.updatePreAuthDrafts({
                    username: String(this.preAuthUsernameValue?.value ?? ''),
                    roomName: String(this.preAuthRoomValue?.value ?? ''),
                    roomPassword: String(this.preAuthRoomPasswordValue?.value ?? ''),
                    sharemic: !!this.preAuthShareMicVariable?.value,
                    headlessmode: !!this.preAuthHeadlessVariable?.value,
                });
            }
            return true;
        }

        const remoteNavSignature = this.getRemoteNavSignature();
        if (remoteNavSignature !== this.lastRemoteNavSignature) {
            this.lastRemoteNavSignature = remoteNavSignature;
            this.ensureCurrentVrSubmenuVisible();
            void this.rebuildRoot();
            return false;
        }

        if (this.currentVrSubmenu === Submenu.RemoteDeviceClone) {
            const remoteCloneControlsSignature = this.getRemoteCloneControlsSignature();
            if (remoteCloneControlsSignature !== this.lastRemoteCloneControlsSignature) {
                this.lastRemoteCloneControlsSignature = remoteCloneControlsSignature;
                void this.rebuildRoot();
                return false;
            }
        }

        this.syncDynamicState();
        if (this.deviceNeedsRebuild && this.currentVrSubmenu === Submenu.Device) {
            this.deviceNeedsRebuild = false;
            void this.rebuildRoot();
        }
        return true;
    }

    private initMenuSlideState(): void {
        const pos = this.object.getPositionLocal();
        this.menuSlideBaseY = pos[1] ?? 0;
        this.menuSlideBaseZ = pos[2] ?? 0;
        this.menuSlideCurrentX = this.menuSlideDefaultX;
        this.menuSlideTargetX = this.menuSlideDefaultX;
        this.menuSlideFromX = this.menuSlideDefaultX;
        this.applyMenuSlideX(this.menuSlideDefaultX);
    }

    private resolveKeyboardRootComponent(): void {
        if (this.keyboardRootComponent) {
            return;
        }

        let root: Object3D | null = this.object;
        while (root?.parent) {
            root = root.parent;
        }

        if (!root) {
            return;
        }

        const found = this.findComponentRecursive(root, this.keyboardRootComponentName);
        if (found) {
            this.keyboardRootComponent = found as { root?: { enabled?: boolean } | null; active?: boolean };
        }
    }

    private findComponentRecursive(start: Object3D, componentTypeName: string): unknown | null {
        const component = start.getComponent(componentTypeName as never) as unknown | null;
        if (component) {
            return component;
        }

        for (const child of start.children) {
            const childMatch = this.findComponentRecursive(child, componentTypeName);
            if (childMatch) {
                return childMatch;
            }
        }

        return null;
    }

    private isKeyboardVisible(): boolean {
        this.resolveKeyboardRootComponent();
        if (!this.keyboardRootComponent) {
            return false;
        }

        const rootEnabled = this.keyboardRootComponent.root?.enabled;
        if (typeof rootEnabled === 'boolean') {
            return rootEnabled;
        }

        return !!this.keyboardRootComponent.active;
    }

    private isAvatarPreviewVisible(): boolean {
        return this._menuVisible
            && this.activeMenuMode === 'submenu'
            && this.currentVrSubmenu === Submenu.Avatar
            && !!getAvatarCatalog()[this.avatarPreviewIndex];
    }

    private syncMenuSlideToKeyboard(): void {
        const keyboardVisible = this.isKeyboardVisible();
        const avatarPreviewVisible = this.isAvatarPreviewVisible();
        const slideStateSignature = `${avatarPreviewVisible ? 1 : 0}:${keyboardVisible ? 1 : 0}`;
        const offsetX = avatarPreviewVisible
            ? this.menuSlideAvatarPreviewVisibleX
            : keyboardVisible
                ? this.menuSlideKeyboardVisibleX
                : 0;
        const targetX = this.menuSlideDefaultX + offsetX;
        const slideDurationMs = Math.max(1, Math.round(this.menuSlideDurationSeconds * 1000));

        if (targetX !== this.menuSlideTargetX) {
            this.menuSlideFromX = this.menuSlideCurrentX;
            this.menuSlideTargetX = targetX;
            this.menuSlideStartMs = Date.now();
            this.menuSlideAnimating = true;
        }

        if (this.menuSlideAnimating) {
            const elapsed = Date.now() - this.menuSlideStartMs;
            const t = Math.max(0, Math.min(1, elapsed / slideDurationMs));
            const eased = 1 - Math.pow(1 - t, 3);
            this.menuSlideCurrentX = this.menuSlideFromX + (this.menuSlideTargetX - this.menuSlideFromX) * eased;
            this.applyMenuSlideX(this.menuSlideCurrentX);
            if (t >= 1) {
                this.menuSlideAnimating = false;
            }
        } else if (this.menuSlideStateSignatureLast !== slideStateSignature) {
            this.menuSlideCurrentX = this.menuSlideTargetX;
            this.applyMenuSlideX(this.menuSlideCurrentX);
        }

        this.keyboardVisibleLast = keyboardVisible;
        this.menuSlideStateSignatureLast = slideStateSignature;
    }

    private applyMenuSlideX(x: number): void {
        this.object.setPositionLocal([x, this.menuSlideBaseY, this.menuSlideBaseZ]);
    }

    override afterWidgetUpdate(): void {
        if (this._menuVisible) {
            this.bindRootTextureToMaterial();
        }
    }

    private createButton(label: string, handler: () => void): TextButton {
        const theme = this.getMenuThemeColors();
        const buttonCornerRadius = Math.max(4, Math.min(26, this.vrMenuButtonCornerRadius));
        const buttonInnerPaddingHorizontal = Math.max(2, Math.min(24, this.vrMenuButtonInnerPadding));
        const buttonFontSize = Math.max(12, Math.min(30, this.vrMenuButtonFontSize));
        const buttonMinHeight = Math.max(22, Math.min(84, this.vrMenuButtonMinHeight));
        const buttonMinWidth = Math.max(12, Math.min(220, this.vrMenuButtonMinWidth));
        const buttonVerticalPaddingOverride = this.vrMenuButtonInnerPaddingVertical;
        let buttonVerticalPadding = buttonVerticalPaddingOverride >= 0
            ? Math.max(0, Math.min(14, Math.round(buttonVerticalPaddingOverride)))
            : Math.max(1, Math.min(14, Math.round(buttonInnerPaddingHorizontal * 0.45)));
        const maxPadFromHeight = Math.max(2, Math.floor((buttonMinHeight - buttonFontSize) / 2) - 1);
        buttonVerticalPadding = Math.min(buttonVerticalPadding, maxPadFromHeight);
        const button = new RoundedTextButton(label, {
            minHeight: buttonMinHeight,
            minWidth: buttonMinWidth,
            maxHeight: buttonMinHeight,
            containerPadding: {
                left: buttonInnerPaddingHorizontal,
                right: buttonInnerPaddingHorizontal,
                top: buttonVerticalPadding,
                bottom: buttonVerticalPadding,
            },
            bodyTextFont: `600 ${buttonFontSize}px "Rajdhani", "Orbitron", sans-serif`,
            bodyTextFill: theme.textFill,
            primaryFill: theme.buttonFill,
            accentFill: theme.buttonHighlightFill,
            backgroundFill: theme.buttonFill,
            backgroundGlowFill: theme.buttonHighlightFill,
            roundedCornersRadii: buttonCornerRadius,
        });
        button.on('click', handler);
        return button;
    }

    private createCheckbox(variable: Variable<boolean>): Checkbox {
        const checkboxLength = Math.max(12, Math.min(48, this.vrMenuCheckboxLength));
        const checkboxInnerPadding = Math.max(1, Math.min(10, this.vrMenuCheckboxInnerPadding));
        return new Checkbox(variable, {
            checkboxLength,
            checkboxInnerPadding,
            minWidth: checkboxLength,
            minHeight: checkboxLength,
        });
    }

    private createTableHeaderRow(
        columns: Array<{ label: string; width: number }>,
        rowHeight: number,
        rightGap: number,
    ): Row {
        const cells: Widget[] = columns.map((column) => new Label(column.label, {
            minWidth: column.width,
            maxWidth: column.width,
            minHeight: rowHeight,
            maxHeight: rowHeight,
            bodyTextAlign: TextAlignMode.Center,
        }));
        return this.createTableDataRow(cells, rowHeight, rightGap);
    }

    private createTableDataRow(cells: Widget[], rowHeight: number, rightGap: number): Row {
        const gap = Math.max(0, rightGap);
        if (gap > 0) {
            cells.push(new Label('', {
                minWidth: gap,
                maxWidth: gap,
                minHeight: rowHeight,
                maxHeight: rowHeight,
            }));
        }

        return new Row(cells, {
            multiContainerAlignment: { main: FlexAlignment.Center, cross: Alignment.Center },
            multiContainerSpacing: 4,
        });
    }

    private createScrollableListViewport(
        listChildren: Widget[],
        viewportHeight: number,
        scrollbarMode: ScrollbarMode,
        viewportWidth?: number,
    ): ScrollableViewportWidget {
        const viewportProps: Record<string, unknown> = {
            widthCoupling: AxisCoupling.Bi,
            scrollbarMode,
            minHeight: viewportHeight,
            maxHeight: viewportHeight,
        };

        if (typeof viewportWidth === 'number') {
            viewportProps.minWidth = viewportWidth;
            viewportProps.maxWidth = viewportWidth;
        }

        const listColumnProps: Record<string, unknown> = {
            multiContainerSpacing: 6,
            containerPadding: { left: 0, right: 0, top: 0, bottom: 0 },
        };

        return new ScrollableViewportWidget(new Column(listChildren, listColumnProps), viewportProps);
    }

    private createTableCellButton(label: string, handler: () => void, width: number, selected: boolean): TextButton {
        const theme = this.getMenuThemeColors();
        const rowHeight = Math.max(22, Math.min(64, this.vrMenuTableRowHeight));
        const buttonCornerRadius = Math.max(4, Math.min(26, this.vrMenuButtonCornerRadius));
        const baseInnerPadding = Math.max(2, Math.min(24, this.vrMenuButtonInnerPadding));
        const horizontalPadding = Math.max(1, Math.min(14, Math.round(baseInnerPadding * 0.55)));
        const tableFontSize = Math.max(11, Math.min(24, this.vrMenuButtonFontSize - 3));
        const buttonVerticalPaddingOverride = this.vrMenuButtonInnerPaddingVertical;
        let verticalPadding = buttonVerticalPaddingOverride >= 0
            ? Math.max(0, Math.min(8, Math.round(buttonVerticalPaddingOverride)))
            : Math.max(1, Math.min(8, Math.round(horizontalPadding * 0.4)));
        const maxPadFromHeight = Math.max(1, Math.floor((rowHeight - tableFontSize) / 2) - 1);
        verticalPadding = Math.min(verticalPadding, maxPadFromHeight);

        const btn = new RoundedTextButton(label, {
            minWidth: width,
            maxWidth: width,
            minHeight: rowHeight,
            maxHeight: rowHeight,
            containerPadding: {
                left: horizontalPadding,
                right: horizontalPadding,
                top: verticalPadding,
                bottom: verticalPadding,
            },
            bodyTextFont: `600 ${tableFontSize}px "Rajdhani", "Orbitron", sans-serif`,
            bodyTextFill: theme.textFill,
            primaryFill: theme.buttonFill,
            accentFill: theme.buttonHighlightFill,
            backgroundFill: theme.buttonFill,
            backgroundGlowFill: theme.buttonHighlightFill,
            roundedCornersRadii: buttonCornerRadius,
        });
        if (selected) {
            (btn as unknown as { forced?: boolean }).forced = true;
        }
        btn.on('click', handler);
        return btn;
    }

    private createSubheadingLabel(text: string): Label {
        return new Label(text, {
            bodyTextFont: '700 22px "Orbitron", "Rajdhani", sans-serif',
            bodyTextFill: this.getMenuThemeColors().textFill,
            minHeight: 28,
        });
    }

    private createVerticalSpacer(heightPx: number): Label {
        const height = Math.max(0, Math.round(heightPx));
        return new Label('', {
            minHeight: height,
            maxHeight: height,
        });
    }

    private getMenuThemeColors(): {
        textFill: string;
        panelFill: string;
        outerPanelFill: string;
        inputFill: string;
        buttonFill: string;
        buttonHighlightFill: string;
        borderFill: string;
        borderGlowFill: string;
    } {
        const panelOpacity = Math.max(0.55, Math.min(1.0, this.vrMenuPanelOpacity));
        const borderOpacity = Math.max(0.15, Math.min(0.9, this.vrMenuBorderOpacity));
        const accentStrength = Math.max(0.5, Math.min(1.5, this.vrMenuAccentStrength));

        const bg = this.readThemeColor(this.vrMenuBackgroundColor, [0.094, 0.173, 0.278, 1.0]);
        const btn = this.readThemeColor(this.vrMenuButtonColor, [0.133, 0.486, 0.824, 1.0]);
        const txt = this.readThemeColor(this.vrMenuFontColor, [0.941, 0.973, 1.0, 1.0]);
        const hover = this.readThemeColor(this.vrMenuButtonHighlightColor, [0.424, 0.824, 1.0, 1.0]);
        const border = this.readThemeColor(this.vrMenuBorderColor, [0.384, 0.69, 0.91, 1.0]);
        const glow = this.readThemeColor(this.vrMenuBorderGlowColor, [0.659, 0.875, 1.0, 1.0]);

        const buttonAlphaScale = Math.max(0.45, Math.min(1.0, 0.44 + 0.22 * accentStrength));
        const hoverAlphaScale = Math.max(0.55, Math.min(1.0, 0.58 + 0.30 * accentStrength));
        const glowOpacity = Math.max(0.10, Math.min(0.6, borderOpacity * 0.7));

        return {
            textFill: this.colorToRgba(txt),
            panelFill: this.colorToRgba(bg, panelOpacity),
            outerPanelFill: this.colorToRgba(bg, Math.max(0.7, panelOpacity * 0.95)),
            inputFill: this.colorToRgba(bg, Math.max(0.86, panelOpacity * 1.05)),
            buttonFill: this.colorToRgba(btn, buttonAlphaScale),
            buttonHighlightFill: this.colorToRgba(hover, hoverAlphaScale),
            borderFill: this.colorToRgba(border, borderOpacity),
            borderGlowFill: this.colorToRgba(glow, glowOpacity),
        };
    }

    private readThemeColor(value: unknown, fallback: ThemeColor): ThemeColor {
        if (Array.isArray(value) || value instanceof Float32Array) {
            const r = Number((value as number[])[0]);
            const g = Number((value as number[])[1]);
            const b = Number((value as number[])[2]);
            const a = Number((value as number[])[3]);
            if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b) && Number.isFinite(a)) {
                return [
                    Math.max(0, Math.min(1, r)),
                    Math.max(0, Math.min(1, g)),
                    Math.max(0, Math.min(1, b)),
                    Math.max(0, Math.min(1, a)),
                ];
            }
        }
        return fallback;
    }

    private colorToRgba(color: ThemeColor, alphaMultiplier = 1): string {
        const r = Math.round(Math.max(0, Math.min(1, color[0])) * 255);
        const g = Math.round(Math.max(0, Math.min(1, color[1])) * 255);
        const b = Math.round(Math.max(0, Math.min(1, color[2])) * 255);
        const alpha = Math.max(0, Math.min(1, color[3] * alphaMultiplier));
        return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
    }

    private createFormFieldLabel(text: string, width = 220): Label {
        return new Label(text, {
            minWidth: width,
            maxWidth: width,
        });
    }

    private createSpacedColumn(children: Widget[], spacing: number): Column {
        const options: Record<string, unknown> = {
            multiContainerSpacing: spacing,
        };
        return new Column(children, options);
    }

    private createStartAlignedRow(children: Widget[]): Row {
        return new Row(children, {
            multiContainerAlignment: { main: FlexAlignment.Start, cross: Alignment.Center },
        });
    }

    private createLabeledControlRow(label: string, control: Widget, labelWidth = 220): Row {
        return this.createStartAlignedRow([
            this.createFormFieldLabel(label, labelWidth),
            control,
        ]);
    }

    private syncSchema(): void {
        if (!this._schema) {
            this._schema = createVrMainMenuSchema();
        }

        if (this.titleLabel) {
            this.titleLabel.text = this._schema.title;
        }
    }

    private syncDynamicState(): void {
        const eventsClient = EventsClient.instance;
        if (!eventsClient) {
            return;
        }
        switch (this.currentVrSubmenu) {
            case Submenu.Main: this.syncMainState(eventsClient); break;
            case Submenu.Device: this.syncDeviceState(); break;
            case Submenu.Avatar: this.syncAvatarState(); break;
            case Submenu.RemoteDeviceClone: this.syncRemoteCloneState(); break;
            case Submenu.Scene: this.syncSceneState(); break;
            case Submenu.Account:
                if (this.accountLoadState === 'idle') {
                    void this.loadSubmenuData(Submenu.Account);
                }
                break;
            case Submenu.Entertainers:
                if (this.entertainersLoadState === 'idle') {
                    void this.loadSubmenuData(Submenu.Entertainers);
                }
                break;
            case Submenu.Rooms:
                if (this.roomsLoadState === 'idle') {
                    void this.loadSubmenuData(Submenu.Rooms);
                }
                break;
            case Submenu.UserAdmin:
                if (this.userAdminLoadState === 'idle') {
                    void this.loadSubmenuData(Submenu.UserAdmin);
                }
                break;
            default: break;
        }
    }

    private syncMainState(eventsClient: EventsClient): void {
        if (this.broadcastButton) {
            this.setButtonText(this.broadcastButton, eventsClient.sharingVideo ? 'Stop Broadcasting' : 'Broadcast');
        }
        if (this.serverBroadcastVariable && this.serverBroadcastVariable.value !== !!eventsClient.serverBasedBroadcast) {
            this.serverBroadcastVariable.value = !!eventsClient.serverBasedBroadcast;
        }
        if (this.audioMixLabel) {
            const gain = eventsClient.getBroadcastShareAudioMixGain();
            this.audioMixLabel.text = `Shared Audio in Group Mix: ${gain.toFixed(2)}x`;
            if (this.audioMixVariable && Math.abs(this.audioMixVariable.value - gain) > 0.001) {
                this.audioMixVariable.value = gain;
            }
        }
        if (this.resyncPingsVariable && this.resyncPingsVariable.value !== !!this.resyncPingsEnabled) {
            this.resyncPingsVariable.value = !!this.resyncPingsEnabled;
        }
    }

    private getLastCommandForDevice(device: any, action: SupportedAction): { actuateVals?: string[] } | null {
        if (typeof device?.getLastCommand === 'function') {
            return device.getLastCommand(action) ?? null;
        }

        const deviceIndex = Number(device?.index);
        if (!Number.isFinite(deviceIndex)) {
            return null;
        }

        return IntifaceController.instance.deviceLastCommandMap.get(deviceIndex)?.get(action) ?? null;
    }

    private getActuatorSteps(device: any, action: SupportedAction, attributeIndex: number): number {
        switch (action) {
            case SupportedAction.Vibrate:
                return Math.max(1, IntifaceController.instance.getVibeSteps(device, attributeIndex) || 1);
            case SupportedAction.Rotate:
                return Math.max(1, IntifaceController.instance.getRotateSteps(device, attributeIndex) || 1);
            case SupportedAction.Scalar:
                return Math.max(1, IntifaceController.instance.getScaleSteps(device, attributeIndex) || 1);
            default:
                return 1;
        }
    }

    private getLivePercentFromDevices(devices: any[], action: SupportedAction): number {
        let maxNormalizedValue = 0;
        for (const device of devices) {
            const lastCommand = this.getLastCommandForDevice(device, action);
            const vals = Array.isArray(lastCommand?.actuateVals) ? lastCommand.actuateVals : [];
            for (let idx = 0; idx < vals.length; idx++) {
                const raw = Number(vals[idx]);
                if (!Number.isFinite(raw)) {
                    continue;
                }
                const steps = this.getActuatorSteps(device, action, idx);
                const normalized = Math.max(0, Math.min(1, raw / steps));
                if (normalized > maxNormalizedValue) {
                    maxNormalizedValue = normalized;
                }
            }
        }
        return Math.round(maxNormalizedValue * 100);
    }

    private syncDeviceState(): void {
        const ic = IntifaceController.instance;
        if (!ic) { return; }
        const deviceSchema = createDeviceMenuSchema();
        if (this.deviceStatusLabel) {
            this.deviceStatusLabel.text = ic.connected ? 'Status: Connected' : ic.connecting ? 'Status: Connecting…' : 'Status: Disconnected';
        }
        if (this.deviceConnectButton) {
            this.setButtonText(
                this.deviceConnectButton,
                ic.connected
                    ? DEVICE_MENU_LABELS.disconnect
                    : ic.connecting
                        ? DEVICE_MENU_LABELS.connecting
                        : readDeviceSchemaButtonLabel(deviceSchema, DEVICE_MENU_ACTIONS.connectToIntiface, DEVICE_MENU_LABELS.connect),
            );
        }
        if (this.deviceModeLabel) {
            this.deviceModeLabel.text = `Control mode: ${ic.deviceControlMode}`;
        }

        if (this.deviceEnvironmentButton) {
            this.setButtonText(
                this.deviceEnvironmentButton,
                readDeviceSchemaButtonLabel(deviceSchema, DEVICE_MENU_ACTIONS.enterEnvironment, DEVICE_MENU_LABELS.environment),
            );
        }
        if (this.deviceLeaveEnvironmentButton) {
            this.setButtonText(
                this.deviceLeaveEnvironmentButton,
                readDeviceSchemaButtonLabel(deviceSchema, DEVICE_MENU_ACTIONS.leaveEnvironment, DEVICE_MENU_LABELS.leaveEnvironment),
            );
        }
        if (this.deviceHapticsHudButton) {
            const hudEnabled = EventsClient.instance?.isHapticDisplayEnabled?.() ?? true;
            this.setButtonText(this.deviceHapticsHudButton, hudEnabled ? DEVICE_MENU_LABELS.hideHapticsHud : DEVICE_MENU_LABELS.showHapticsHud);
        }

        const prostheticState = EventsClient.instance?.getLocalProstheticState?.() ?? { type: 'none' as const };
        if (this.deviceProstheticNoneButton) {
            this.setButtonText(this.deviceProstheticNoneButton, prostheticState.type === 'none' ? `${DEVICE_MENU_LABELS.prostheticNone} (Active)` : DEVICE_MENU_LABELS.prostheticNone);
        }
        if (this.deviceProstheticInnyButton) {
            this.setButtonText(this.deviceProstheticInnyButton, prostheticState.type === 'inny' ? `${DEVICE_MENU_LABELS.prostheticInny} (Active)` : DEVICE_MENU_LABELS.prostheticInny);
        }
        if (this.deviceProstheticOutyButton) {
            this.setButtonText(this.deviceProstheticOutyButton, prostheticState.type === 'outy' ? `${DEVICE_MENU_LABELS.prostheticOuty} (Active)` : DEVICE_MENU_LABELS.prostheticOuty);
        }

        const devices = Array.from(ic.getDevices());
        if (this.deviceListLabel) {
            if (devices.length > 0) {
                this.deviceListLabel.text = `Devices: ${devices.map((d: any) => String(d.name ?? 'Unknown')).join(', ')}`;
            } else {
                this.deviceListLabel.text = ic.connected ? DEVICE_MENU_LABELS.noDevicesDetected : DEVICE_MENU_LABELS.connectToSeeDevices;
            }
        }

        const liveVibePercent = this.getLivePercentFromDevices(devices as any[], SupportedAction.Vibrate);
        if (this.deviceVibVariable && Math.round(this.deviceVibVariable.value) !== liveVibePercent) {
            this.deviceVibVariable.value = liveVibePercent;
        }
        if (this.deviceVibLabel) {
            this.deviceVibLabel.text = `Vibration: ${liveVibePercent}%`;
        }
    }

    private syncRemoteCloneState(): void {
        const peerId = this.currentRemotePeerId;
        if (peerId === null) {
            if (this.remoteCloneStatusLabel) {
                this.remoteCloneStatusLabel.text = 'No remote peer selected.';
            }
            if (this.remoteCloneListLabel) {
                this.remoteCloneListLabel.text = '';
            }
            return;
        }

        if (this.remoteCloneStatusLabel) {
            const username = EventsClient.instance?.remoteUsernames?.get(peerId) ?? `Remote ${peerId}`;
            this.remoteCloneStatusLabel.text = `Connected: ${username} (peerID: ${peerId}) | Control mode: ${IntifaceController.instance.deviceControlMode}`;
        }

        if (this.remoteCloneListLabel) {
            const devices = Array.from(IntifaceController.instance.getDevices(peerId));
            this.remoteCloneListLabel.text = devices.length > 0
                ? `Devices: ${devices.map((d: any) => String(d.name ?? 'Unknown')).join(', ')}`
                : 'No remote devices detected yet.';
        }

        // Mirror web behavior: reflect current remote command state in the control UI,
        // including commands produced by Environment mode effects.
        const devices = Array.from(IntifaceController.instance.getDevices(peerId));
        const livePercent = this.getLivePercentFromDevices(devices as any[], SupportedAction.Vibrate);
        if (IntifaceController.instance.deviceControlMode === DeviceControlMode.Environment
            && this.remoteCloneVibVariable
            && Math.round(this.remoteCloneVibVariable.value) !== livePercent) {
            this.remoteCloneVibVariable.value = livePercent;
        }
        if (this.remoteCloneVibLabel) {
            this.remoteCloneVibLabel.text = `Vibration: ${livePercent}%`;
        }
    }

    private syncSceneState(): void {
        const sc = SceneController.instance;
        const currentRef = sc?.currentSceneRef ?? '';
        for (const { sceneRef, button } of this.sceneButtonRefs) {
            const isActive = sceneRef === currentRef;
            const label = isActive ? `▶ ${sceneRef}` : sceneRef;
            this.setButtonText(button, label);
        }
    }

    private submitUrlPlayback(): void {
        const url = String(this.urlValue?.value ?? '').trim();

        if (!url) {
            Logger.warn(MODULE, 'Cannot start URL playback: empty URL');
            return;
        }

        MenuController.instance.onUserChoice.notify('broadcast-url-media', {
            url,
            enableResyncPings: this.resyncPingsEnabled,
        });
    }

    // ── Async data helpers ──────────────────────────────────────────────────

    private async loadSubmenuData(sub: Submenu): Promise<void> {
        switch (sub) {
            case Submenu.Account: await this.loadAccountData(); break;
            case Submenu.Entertainers: await this.loadEntertainersData(); break;
            case Submenu.Rooms: await this.loadRoomsData(); break;
            case Submenu.UserAdmin: await this.loadUserAdminData(); break;
            default: break;
        }
    }

    private async loadAccountData(): Promise<void> {
        if (this.accountLoadState !== 'idle') { return; }
        this.accountLoadState = 'loading';
        const ec = EventsClient.instance;
        const token = ec?.getAuthToken();
        if (!token) {
            this.accountLoadState = 'error';
            if (this.accountUsernameLabel) { this.accountUsernameLabel.text = ACCOUNT_MENU_LABELS.notAuthenticated; }
            return;
        }
        try {
            const resp = await fetch(`${ec.getRestApiBaseUrl()}/api/users/me`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!resp.ok) { this.accountLoadState = 'error'; return; }
            const profile = await resp.json() as { username?: string; display_name?: string | null; email?: string | null; token_balance?: number };
            this.accountData = {
                username: profile.username ?? '',
                display_name: profile.display_name ?? null,
                email: profile.email ?? null,
                token_balance: Number(profile.token_balance ?? 0),
            };
            this.accountLoadState = 'loaded';
            if (this.accountUsernameLabel) {
                this.accountUsernameLabel.text = `${ACCOUNT_MENU_LABELS.usernamePrefix} ${this.accountData.username}`;
            }
            if (this.accountTokenBalanceLabel) {
                this.accountTokenBalanceLabel.text = `${ACCOUNT_MENU_LABELS.tokenBalancePrefix} ${this.accountData.token_balance}`;
            }
            if (this.accountDisplayNameValue && !this.accountDisplayNameValue.value && this.accountData.display_name) {
                this.accountDisplayNameValue.value = this.accountData.display_name;
            }
            if (this.accountEmailValue && !this.accountEmailValue.value && this.accountData.email) {
                this.accountEmailValue.value = this.accountData.email;
            }
            this.root?.update();
        } catch {
            this.accountLoadState = 'error';
        }
    }

    private async loadEntertainersData(): Promise<void> {
        if (this.entertainersLoadState !== 'idle') { return; }
        this.entertainersLoadState = 'loading';
        const ec = EventsClient.instance;
        const token = ec?.getAuthToken();
        if (!token) {
            this.entertainersLoadState = 'error';
            if (this.entertainersStatusLabel) { this.entertainersStatusLabel.text = ENTERTAINERS_MENU_LABELS.notAuthenticated; }
            return;
        }

        try {
            const resp = await fetch(`${ec.getRestApiBaseUrl()}/api/marketplace/entertainers`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                this.entertainersLoadState = 'error';
                if (this.entertainersStatusLabel) {
                    this.entertainersStatusLabel.text = String((data as { error?: string }).error ?? 'Failed to load entertainers.');
                }
                return;
            }

            const payload = data as {
                my_token_balance?: number;
                entertainers?: Array<{ id: string; username: string; display_name: string | null }>;
            };

            this.entertainersMyBalance = Number(payload.my_token_balance ?? 0);
            this.entertainersData = Array.isArray(payload.entertainers) ? payload.entertainers : [];
            if (this.entertainersSelectedUserId && !this.entertainersData.some((entry) => entry.id === this.entertainersSelectedUserId)) {
                this.entertainersSelectedUserId = null;
            }
            this.entertainersLoadState = 'loaded';

            if (this.currentVrSubmenu === Submenu.Entertainers) {
                void this.rebuildRoot();
                return;
            }
            this.root?.update();
        } catch {
            this.entertainersLoadState = 'error';
        }
    }

    private async sendEntertainerTip(): Promise<void> {
        if (!this.entertainersSelectedUserId) {
            if (this.entertainersStatusLabel) {
                this.entertainersStatusLabel.text = ENTERTAINERS_MENU_LABELS.selectEntertainer;
            }
            this.root?.update();
            return;
        }

        const amount = Number(String(this.entertainersTipAmountValue?.value ?? '').trim());
        if (!Number.isInteger(amount) || amount <= 0) {
            if (this.entertainersStatusLabel) {
                this.entertainersStatusLabel.text = 'Tip amount must be a positive whole number.';
            }
            this.root?.update();
            return;
        }

        if (amount > this.entertainersMyBalance) {
            if (this.entertainersStatusLabel) {
                this.entertainersStatusLabel.text = 'Tip amount exceeds your token balance.';
            }
            this.root?.update();
            return;
        }

        const ec = EventsClient.instance;
        const token = ec?.getAuthToken();
        if (!token) {
            return;
        }

        try {
            const resp = await fetch(`${ec.getRestApiBaseUrl()}/api/marketplace/tips`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    entertainer_user_id: this.entertainersSelectedUserId,
                    amount,
                }),
            });

            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                if (this.entertainersStatusLabel) {
                    this.entertainersStatusLabel.text = String((data as { error?: string }).error ?? 'Tip failed.');
                }
                this.root?.update();
                return;
            }

            const payload = data as {
                message?: string;
                sender?: { balance_after?: number };
                recipient?: { id?: string };
            };

            this.entertainersMyBalance = Number(payload.sender?.balance_after ?? Math.max(0, this.entertainersMyBalance - amount));

            this.entertainersTipAmountValue.value = '';
            if (this.entertainersStatusLabel) {
                this.entertainersStatusLabel.text = payload.message || `Tip sent (${amount} token${amount === 1 ? '' : 's'}).`;
            }

            this.entertainersLoadState = 'idle';
            void this.loadEntertainersData();
        } catch {
            if (this.entertainersStatusLabel) {
                this.entertainersStatusLabel.text = 'Error sending tip.';
            }
        }

        this.root?.update();
    }

    private async saveProfile(): Promise<void> {
        const displayName = String(this.accountDisplayNameValue?.value ?? '').trim() || null;
        const email = String(this.accountEmailValue?.value ?? '').trim() || null;
        const ec = EventsClient.instance;
        const token = ec?.getAuthToken();
        if (!token) { return; }
        try {
            const resp = await fetch(`${ec.getRestApiBaseUrl()}/api/users/me`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ display_name: displayName, email }),
            });
            if (this.accountStatusLabel) {
                this.accountStatusLabel.text = resp.ok ? 'Changes saved.' : ACCOUNT_MENU_LABELS.saveFailed;
            }
            this.root?.update();
        } catch {
            if (this.accountStatusLabel) { this.accountStatusLabel.text = ACCOUNT_MENU_LABELS.networkError; }
        }
    }

    private isAccountPasswordFormSubmittable(): boolean {
        const currentPassword = String(this.accountCurrentPasswordValue?.value ?? '');
        const newPassword = String(this.accountNewPasswordValue?.value ?? '');
        const confirmPassword = String(this.accountConfirmPasswordValue?.value ?? '');
        return currentPassword.length > 0 && newPassword.length >= 8 && newPassword === confirmPassword;
    }

    private async changePassword(): Promise<void> {
        const currentPassword = String(this.accountCurrentPasswordValue?.value ?? '');
        const newPassword = String(this.accountNewPasswordValue?.value ?? '');
        const confirmPassword = String(this.accountConfirmPasswordValue?.value ?? '');

        if (newPassword !== confirmPassword) {
            if (this.accountPasswordStatusLabel) {
                this.accountPasswordStatusLabel.text = 'New password and confirmation do not match.';
            }
            this.root?.update();
            return;
        }

        if (newPassword.length < 8) {
            if (this.accountPasswordStatusLabel) {
                this.accountPasswordStatusLabel.text = 'New password must be at least 8 characters.';
            }
            this.root?.update();
            return;
        }

        if (!this.isAccountPasswordFormSubmittable()) {
            if (this.accountPasswordStatusLabel) {
                this.accountPasswordStatusLabel.text = 'Please fill all password fields.';
            }
            this.root?.update();
            return;
        }

        const ec = EventsClient.instance;
        const token = ec?.getAuthToken();
        if (!token) { return; }

        try {
            const resp = await fetch(`${ec.getRestApiBaseUrl()}/api/users/me/password`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword,
                }),
            });

            if (!resp.ok) {
                let message = 'Password update failed.';
                try {
                    const data = await resp.json() as { error?: string };
                    const errorText = String(data.error ?? '').toLowerCase();
                    if (resp.status === 401 && errorText.includes('current password')) {
                        message = 'Invalid Current Password';
                    } else if (data.error) {
                        message = data.error;
                    }
                } catch {
                    // Use default message above
                }
                if (this.accountPasswordStatusLabel) {
                    this.accountPasswordStatusLabel.text = message;
                }
                this.root?.update();
                return;
            }

            this.accountCurrentPasswordValue.value = '';
            this.accountNewPasswordValue.value = '';
            this.accountConfirmPasswordValue.value = '';
            if (this.accountPasswordStatusLabel) {
                this.accountPasswordStatusLabel.text = 'Password change successful.';
            }
            this.root?.update();
        } catch {
            if (this.accountPasswordStatusLabel) {
                this.accountPasswordStatusLabel.text = 'Error updating password.';
            }
            this.root?.update();
        }
    }

    private async loadRoomsData(): Promise<void> {
        if (this.roomsLoadState !== 'idle') { return; }
        this.roomsLoadState = 'loading';
        // Match web behavior: each refresh/load clears transient room selection state.
        this.roomsSelectedRoomName = null;
        this.roomsJoinPasswordDraft = '';
        if (this.roomsJoinPasswordValue) {
            this.roomsJoinPasswordValue.value = '';
        }
        const ec = EventsClient.instance;
        const token = ec?.getAuthToken();
        if (!token) {
            this.roomsLoadState = 'error';
            if (this.roomsStatusLabel) { this.roomsStatusLabel.text = ROOMS_MENU_LABELS.notAuthenticated; }
            return;
        }
        try {
            const resp = await fetch(`${ec.getRestApiBaseUrl()}/api/rooms`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!resp.ok) { this.roomsLoadState = 'error'; return; }
            this.roomsData = (await resp.json()) as Array<{ name: string; isPrivate: boolean; userCount: number }>;
            if (this.roomsSelectedRoomName && !this.roomsData.some((r) => r.name === this.roomsSelectedRoomName)) {
                this.roomsSelectedRoomName = null;
            }
            this.roomsLoadState = 'loaded';
            if (this.currentVrSubmenu === Submenu.Rooms) {
                void this.rebuildRoot();
                return;
            }
            this.root?.update();
        } catch {
            this.roomsLoadState = 'error';
            if (this.roomsStatusLabel) { this.roomsStatusLabel.text = ROOMS_MENU_LABELS.failedToLoadRooms; }
        }
    }

    private async joinSelectedRoom(): Promise<void> {
        const name = String(this.roomsSelectedRoomName ?? '').trim();
        const password = String(this.roomsJoinPasswordValue?.value ?? '');
        if (!name) {
            if (this.roomsStatusLabel) {
                this.roomsStatusLabel.text = 'Select a room to join.';
            }
            this.root?.update();
            return;
        }
        const ec = EventsClient.instance;
        const currentRoom = (ec as any)?.getCurrentRoom?.() ?? '';
        if (name === currentRoom) {
            if (this.roomsStatusLabel) {
                this.roomsStatusLabel.text = 'You are already in this room.';
            }
            this.root?.update();
            return;
        }

        try {
            await (ec as any).switchRoom(name, password);
            if (this.roomsStatusLabel) { this.roomsStatusLabel.text = `Joined room: ${name}`; }
            this.roomsSelectedRoomName = null;
            this.roomsJoinPasswordDraft = '';
            this.roomsJoinPasswordValue.value = '';
            const newCurrentRoom = (ec as any)?.getCurrentRoom?.() ?? name;
            if (this.roomsCurrentLabel) { this.roomsCurrentLabel.text = `${ROOMS_MENU_LABELS.currentRoomPrefix} ${String(newCurrentRoom)}`; }
            this.roomsLoadState = 'idle';
            void this.loadRoomsData();
        } catch (e) {
            const msg = String((e as Error).message ?? e);
            if (this.roomsStatusLabel) {
                if (msg === 'ROOM_PASSWORD_INCORRECT') {
                    this.roomsStatusLabel.text = 'Room password incorrect.';
                } else if (msg === 'ROOM_FULL') {
                    this.roomsStatusLabel.text = 'This room is full.';
                } else if (msg === 'ROOM_LIMIT_REACHED') {
                    this.roomsStatusLabel.text = 'The server has reached its room limit.';
                } else if (msg === 'ROOM_CREATE_COOLDOWN') {
                    this.roomsStatusLabel.text = 'You recently created a room. Please wait before creating another.';
                } else {
                    this.roomsStatusLabel.text = 'Failed to join room.';
                }
            }
        }
        this.root?.update();
    }

    private async createOrJoinRoomFromCreateSection(): Promise<void> {
        const name = String(this.roomsCreateNameValue?.value ?? '').trim();
        const password = String(this.roomsCreatePasswordValue?.value ?? '');
        if (!name) {
            if (this.roomsStatusLabel) {
                this.roomsStatusLabel.text = 'Please enter a room name.';
            }
            this.root?.update();
            return;
        }

        const ec = EventsClient.instance;
        try {
            await (ec as any).switchRoom(name, password);
            this.roomsSelectedRoomName = null;
            this.roomsJoinPasswordDraft = '';
            this.roomsJoinPasswordValue.value = '';
            this.roomsCreateNameDraft = '';
            this.roomsCreatePasswordDraft = '';
            this.roomsCreateNameValue.value = '';
            this.roomsCreatePasswordValue.value = '';
            if (this.roomsStatusLabel) {
                this.roomsStatusLabel.text = `Joined room: ${name}`;
            }
            const newCurrentRoom = (ec as any)?.getCurrentRoom?.() ?? name;
            if (this.roomsCurrentLabel) { this.roomsCurrentLabel.text = `${ROOMS_MENU_LABELS.currentRoomPrefix} ${String(newCurrentRoom)}`; }
            this.roomsLoadState = 'idle';
            void this.loadRoomsData();
        } catch (e) {
            const msg = String((e as Error).message ?? e);
            if (this.roomsStatusLabel) {
                if (msg === 'ROOM_PASSWORD_INCORRECT') {
                    this.roomsStatusLabel.text = 'Room already exists and the password is incorrect.';
                } else if (msg === 'ROOM_FULL') {
                    this.roomsStatusLabel.text = 'This room is full.';
                } else if (msg === 'ROOM_LIMIT_REACHED') {
                    this.roomsStatusLabel.text = 'The server has reached its room limit.';
                } else if (msg === 'ROOM_CREATE_COOLDOWN') {
                    this.roomsStatusLabel.text = 'You recently created a room. Please wait before creating another.';
                } else {
                    this.roomsStatusLabel.text = 'Failed to create or join room.';
                }
            }
        }

        this.root?.update();
    }

    private async loadUserAdminData(): Promise<void> {
        if (this.userAdminLoadState !== 'idle') { return; }
        this.userAdminLoadState = 'loading';
        const ec = EventsClient.instance;
        const token = ec?.getAuthToken();
        if (!token) {
            this.userAdminLoadState = 'error';
            this.userAdminSelectedUserId = null;
            if (this.userAdminStatusLabel) { this.userAdminStatusLabel.text = USER_ADMIN_MENU_LABELS.notAuthenticated; }
            return;
        }
        try {
            const resp = await fetch(`${ec.getRestApiBaseUrl()}/api/admin/users`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!resp.ok) {
                this.userAdminLoadState = 'error';
                if (this.userAdminStatusLabel) {
                    this.userAdminStatusLabel.text = resp.status === 403
                        ? USER_ADMIN_MENU_LABELS.adminAccessRequired
                        : USER_ADMIN_MENU_LABELS.failedToLoad;
                }
                return;
            }
            this.userAdminData = (await resp.json()) as Array<{ id: string; username: string; display_name: string | null; is_user_admin: boolean; is_entertainer: boolean; token_balance: number }>;
            if (this.userAdminSelectedUserId && !this.userAdminData.some((u) => u.id === this.userAdminSelectedUserId)) {
                this.userAdminSelectedUserId = null;
            }
            this.userAdminLoadState = 'loaded';
            if (this.currentVrSubmenu === Submenu.UserAdmin) {
                void this.rebuildRoot();
                return;
            }
            this.root?.update();
        } catch {
            this.userAdminLoadState = 'error';
        }
    }

    private async refreshUserAdminVrVisibility(): Promise<void> {
        const ec = EventsClient.instance;
        const token = ec?.getAuthToken();
        const previousVisible = this.userAdminNavVisible;

        if (!token) {
            this.userAdminNavVisible = false;
            this.userAdminNavResolved = true;
            if (previousVisible !== this.userAdminNavVisible) {
                this.ensureCurrentVrSubmenuVisible();
                await this.rebuildRoot();
            }
            return;
        }

        try {
            const resp = await fetch(`${ec.getRestApiBaseUrl()}/api/users/me`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (!resp.ok) {
                this.userAdminNavVisible = false;
            } else {
                const profile = await resp.json() as { is_user_admin?: boolean };
                this.userAdminNavVisible = !!profile.is_user_admin;
            }
        } catch {
            this.userAdminNavVisible = false;
        }

        this.userAdminNavResolved = true;
        if (previousVisible !== this.userAdminNavVisible) {
            this.ensureCurrentVrSubmenuVisible();
            await this.rebuildRoot();
        }
    }

    private async createAdminUser(): Promise<void> {
        const username = String(this.userAdminUsernameValue?.value ?? '').trim();
        const display_name = String(this.userAdminDisplayNameValue?.value ?? '').trim() || null;
        const password = String(this.userAdminPasswordValue?.value ?? '').trim();
        const is_user_admin = !!this.userAdminIsAdminVariable?.value;
        const is_entertainer = !!this.userAdminIsEntertainerVariable?.value;
        if (!username || !password) {
            if (this.userAdminStatusLabel) { this.userAdminStatusLabel.text = USER_ADMIN_MENU_LABELS.usernameRequired; }
            return;
        }
        const ec = EventsClient.instance;
        const token = ec?.getAuthToken();
        if (!token) { return; }
        try {
            const resp = await fetch(`${ec.getRestApiBaseUrl()}/api/admin/users`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, display_name, password, is_user_admin, is_entertainer }),
            });
            if (this.userAdminStatusLabel) {
                this.userAdminStatusLabel.text = resp.ok ? `User ${username} created.` : USER_ADMIN_MENU_LABELS.failedToCreateUser;
            }
            if (resp.ok) {
                this.userAdminSelectedUserId = null;
                this.userAdminLoadState = 'idle';
                void this.loadUserAdminData();
            }
        } catch {
            if (this.userAdminStatusLabel) { this.userAdminStatusLabel.text = USER_ADMIN_MENU_LABELS.networkError; }
        }
        this.root?.update();
    }

    private clearUserAdminSelection(): void {
        this.userAdminSelectedUserId = null;
        this.userAdminUsernameDraft = '';
        this.userAdminDisplayNameDraft = '';
        this.userAdminPasswordDraft = '';
        this.userAdminNewPasswordDraft = '';
        this.userAdminIsAdminDraft = false;
        this.userAdminIsEntertainerDraft = false;
        this.userAdminSelectedTokenBalance = 0;

        if (this.userAdminUsernameValue) {
            this.userAdminUsernameValue.value = '';
        }
        if (this.userAdminDisplayNameValue) {
            this.userAdminDisplayNameValue.value = '';
        }
        if (this.userAdminPasswordValue) {
            this.userAdminPasswordValue.value = '';
        }
        if (this.userAdminNewPasswordValue) {
            this.userAdminNewPasswordValue.value = '';
        }
        if (this.userAdminIsAdminVariable) {
            this.userAdminIsAdminVariable.value = false;
        }
        if (this.userAdminIsEntertainerVariable) {
            this.userAdminIsEntertainerVariable.value = false;
        }
        if (this.userAdminTokenBalanceLabel) {
            this.userAdminTokenBalanceLabel.text = 'Selected User Token Balance: -';
        }
        if (this.userAdminDepositAmountValue) {
            this.userAdminDepositAmountValue.value = '';
        }

        if (this.userAdminPasswordStatusLabel) {
            this.userAdminPasswordStatusLabel.text = '';
        }
    }

    private async updateSelectedAdminUser(): Promise<void> {
        if (!this.userAdminSelectedUserId) {
            if (this.userAdminStatusLabel) {
                this.userAdminStatusLabel.text = 'Select a user to update.';
            }
            this.root?.update();
            return;
        }

        const username = String(this.userAdminUsernameValue?.value ?? '').trim();
        const display_name = String(this.userAdminDisplayNameValue?.value ?? '').trim() || null;
        const is_user_admin = !!this.userAdminIsAdminVariable?.value;
        const is_entertainer = !!this.userAdminIsEntertainerVariable?.value;
        if (!username) {
            if (this.userAdminStatusLabel) {
                this.userAdminStatusLabel.text = USER_ADMIN_MENU_LABELS.usernameRequired;
            }
            this.root?.update();
            return;
        }

        const ec = EventsClient.instance;
        const token = ec?.getAuthToken();
        if (!token) { return; }

        try {
            const resp = await fetch(`${ec.getRestApiBaseUrl()}/api/admin/users/${encodeURIComponent(this.userAdminSelectedUserId)}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, display_name, is_user_admin, is_entertainer }),
            });

            if (this.userAdminStatusLabel) {
                this.userAdminStatusLabel.text = resp.ok ? 'User updated successfully.' : 'Failed to update user.';
            }

            if (resp.ok) {
                this.userAdminLoadState = 'idle';
                void this.loadUserAdminData();
            }
        } catch {
            if (this.userAdminStatusLabel) {
                this.userAdminStatusLabel.text = 'Error updating user.';
            }
        }

        this.root?.update();
    }

    private async setSelectedAdminUserPassword(): Promise<void> {
        if (!this.userAdminSelectedUserId) {
            if (this.userAdminPasswordStatusLabel) {
                this.userAdminPasswordStatusLabel.text = 'Select a user before setting a password.';
            }
            this.root?.update();
            return;
        }

        const newPassword = String(this.userAdminNewPasswordValue?.value ?? '');
        if (newPassword.length < 8) {
            if (this.userAdminPasswordStatusLabel) {
                this.userAdminPasswordStatusLabel.text = 'New password must be at least 8 characters.';
            }
            this.root?.update();
            return;
        }

        const ec = EventsClient.instance;
        const token = ec?.getAuthToken();
        if (!token) { return; }

        try {
            const resp = await fetch(`${ec.getRestApiBaseUrl()}/api/admin/users/${encodeURIComponent(this.userAdminSelectedUserId)}/password`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ new_password: newPassword }),
            });

            if (this.userAdminPasswordStatusLabel) {
                this.userAdminPasswordStatusLabel.text = resp.ok
                    ? 'Password updated successfully.'
                    : 'Failed to update password.';
            }

            if (resp.ok) {
                this.userAdminNewPasswordDraft = '';
                this.userAdminNewPasswordValue.value = '';
            }
        } catch {
            if (this.userAdminPasswordStatusLabel) {
                this.userAdminPasswordStatusLabel.text = 'Error updating password.';
            }
        }

        this.root?.update();
    }

    private async depositTokensToSelectedAdminUser(): Promise<void> {
        if (!this.userAdminSelectedUserId) {
            if (this.userAdminStatusLabel) {
                this.userAdminStatusLabel.text = 'Select a user before depositing tokens.';
            }
            this.root?.update();
            return;
        }

        const amount = Number(String(this.userAdminDepositAmountValue?.value ?? '').trim());
        if (!Number.isInteger(amount) || amount <= 0) {
            if (this.userAdminStatusLabel) {
                this.userAdminStatusLabel.text = 'Deposit amount must be a positive whole number.';
            }
            this.root?.update();
            return;
        }

        const ec = EventsClient.instance;
        const token = ec?.getAuthToken();
        if (!token) {
            return;
        }

        try {
            const resp = await fetch(`${ec.getRestApiBaseUrl()}/api/admin/users/${encodeURIComponent(this.userAdminSelectedUserId)}/token-deposits`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ amount }),
            });

            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                if (this.userAdminStatusLabel) {
                    this.userAdminStatusLabel.text = String((data as { error?: string }).error ?? 'Failed to deposit tokens.');
                }
                this.root?.update();
                return;
            }

            this.userAdminSelectedTokenBalance = Number((data as { target_balance?: number }).target_balance ?? this.userAdminSelectedTokenBalance + amount);
            if (this.userAdminTokenBalanceLabel) {
                this.userAdminTokenBalanceLabel.text = `Selected User Token Balance: ${this.userAdminSelectedTokenBalance}`;
            }
            this.userAdminDepositAmountValue.value = '';
            if (this.userAdminStatusLabel) {
                this.userAdminStatusLabel.text = `Deposited ${amount} token(s).`;
            }

            this.userAdminLoadState = 'idle';
            void this.loadUserAdminData();
        } catch {
            if (this.userAdminStatusLabel) {
                this.userAdminStatusLabel.text = 'Error depositing tokens.';
            }
        }

        this.root?.update();
    }

    private setButtonText(button: TextButton, value: string): void {
        const buttonAny = button as unknown as {
            child?: { text?: string };
            text?: string;
            markWholeAsDirty?: () => void;
        };

        if (buttonAny.child && typeof buttonAny.child.text === 'string') {
            buttonAny.child.text = value;
            buttonAny.markWholeAsDirty?.();
            return;
        }

        if (typeof buttonAny.text === 'string') {
            buttonAny.text = value;
            buttonAny.markWholeAsDirty?.();
        }
    }

    private bindRootTextureToMaterial(): void {
        const rootAny = this.root as unknown as {
            texture?: { valid?: boolean; width?: number; height?: number } | null;
            dimensions?: [number, number];
            canvasDimensions?: [number, number];
        } | undefined;
        const texture = rootAny?.texture ?? null;
        if (!texture) {
            return;
        }

        const materialAny = (this as unknown as {
            material?: {
                setFlatTexture?: (t: unknown) => void;
                setDiffuseTexture?: (t: unknown) => void;
                flatTexture?: unknown;
                diffuseTexture?: unknown;
            }
        }).material;

        if (!materialAny) {
            return;
        }

        if (typeof materialAny.setFlatTexture === 'function') {
            materialAny.setFlatTexture(texture);
        } else if (typeof materialAny.setDiffuseTexture === 'function') {
            materialAny.setDiffuseTexture(texture);
        } else if (this.textureUniformName === 'flatTexture') {
            materialAny.flatTexture = texture;
        } else if (this.textureUniformName === 'diffuseTexture') {
            materialAny.diffuseTexture = texture;
        }

        if (!this.materialBindingLogged) {
            this.materialBindingLogged = true;
            console.warn(
                `[${MODULE}] bound root texture valid=${String(texture.valid)} size=${String(texture.width)}x${String(texture.height)} rootDims=${String(rootAny?.dimensions?.[0] ?? 0)}x${String(rootAny?.dimensions?.[1] ?? 0)} canvasDims=${String(rootAny?.canvasDimensions?.[0] ?? 0)}x${String(rootAny?.canvasDimensions?.[1] ?? 0)} using uniform=${this.textureUniformName || '<auto>'}`,
            );
        }
    }

    protected override getRootProperties(): Record<string, unknown> {
        const base = super.getRootProperties() as Record<string, unknown>;

        // Keep pointer input enabled even if the editor toggle was disabled.
        base.registerPointerDriver = true;

        // Keep root/canvas sizes conservative for XR browser texture limits.
        // This avoids invalid textures caused by oversized atlas allocations.
        base.constraints = [256, 1024, 160, 1024];
        base.maxCanvasWidth = 1024;
        base.maxCanvasHeight = 1024;
        base.resolution = 1;

        return base;
    }

    private ensureTextureUniformName(): void {
        if (this.textureUniformName && this.textureUniformName.trim().length > 0) {
            Logger.debug(MODULE, `Using configured texture uniform: ${this.textureUniformName}`);
            console.warn(`[${MODULE}] using textureUniformName=${this.textureUniformName}`);
            return;
        }

        const materialCandidate = (this as unknown as { material?: Record<string, unknown> }).material;
        if (!materialCandidate) {
            Logger.warn(MODULE, 'No material assigned yet; unable to auto-detect texture uniform name.');
            return;
        }

        const candidates = ['flatTexture', 'diffuseTexture', 'albedoTexture', 'baseColorTexture', 'mainTexture'];
        const found = candidates.find((key) => key in materialCandidate);

        if (found) {
            this.textureUniformName = found;
            Logger.debug(MODULE, `Auto-selected texture uniform: ${found}`);
            console.warn(`[${MODULE}] auto-selected textureUniformName=${found}`);
            return;
        }

        const pipelineName = String((materialCandidate as { pipeline?: unknown }).pipeline ?? 'unknown');
        Logger.warn(MODULE, `Could not auto-detect texture uniform for pipeline "${pipelineName}". Configure textureUniformName on vr-menu-renderer in editor.`);
        console.warn(`[${MODULE}] failed to auto-detect texture uniform for pipeline=${pipelineName}`);
    }

    private forceHtmlCanvasForLazyWidgets(): void {
        const win = window as unknown as { OffscreenCanvas?: unknown };
        if (typeof win.OffscreenCanvas === 'undefined') {
            return;
        }

        this.offscreenCanvasBackup = win.OffscreenCanvas;
        win.OffscreenCanvas = undefined;
        this.offscreenCanvasPatched = true;
        console.warn(`[${MODULE}] Temporarily disabled OffscreenCanvas to force HTMLCanvas backing.`);
    }

    private restoreOffscreenCanvasPatch(): void {
        if (!this.offscreenCanvasPatched) {
            return;
        }

        const win = window as unknown as { OffscreenCanvas?: unknown };
        win.OffscreenCanvas = this.offscreenCanvasBackup;
        this.offscreenCanvasPatched = false;
        this.offscreenCanvasBackup = undefined;
        console.warn(`[${MODULE}] Restored OffscreenCanvas after root initialization.`);
    }

    private normalizeMaterialTint(): void {
        const material = (this as unknown as { material?: unknown }).material as
            | { setColor?: (value: number[]) => void; color?: number[] }
            | undefined;

        if (!material) {
            return;
        }

        if (typeof material.setColor === 'function') {
            material.setColor([1, 1, 1, 1]);
            return;
        }

        if (Array.isArray(material.color) && material.color.length >= 4) {
            material.color[0] = 1;
            material.color[1] = 1;
            material.color[2] = 1;
            material.color[3] = 1;
        }
    }
}
